import type {
  ApprovalDecisionResponse,
  ApprovalDetailResponse,
  AssetPresignResponse,
  DigestPreviewResponse,
  DigestRunsResponse,
  DocumentChunksResponse,
  DocumentDetailResponse,
  DocumentFiguresResponse,
  DocumentLinkResponse,
  DocumentPageResponse,
  DocumentsBrowseResponse,
  EvidenceGapUpdateResponse,
  HomePayload,
  CompaniesResponse,
  CompanyReadinessResponse,
  CompanyDocumentsResponse,
  CompanyDetailResponse,
  CompanySearchResponse,
  CompanyTierSetResponse,
  CompanyTrackResponse,
  MonitorCreateRequest,
  MonitorDetailResponse,
  MonitorEvaluationsResponse,
  MonitorListRequest,
  MonitorMutationResponse,
  MonitorsResponse,
  MonitorSummary,
  MonitorUpdateRequest,
  OperationsResponse,
  PortfolioBookResponse,
  PortfolioMutationResponse,
  PortfolioResponse,
  PortalSession,
  PositionSide,
  ResearchHistoryResponse,
  ResearchMemoResponse,
  ResearchSeriesArtifact,
  ResearchSeriesResponse,
  ResearchMemosResponse,
  SavedSearch,
  SavedSearchCreateRequest,
  SavedSearchResponse,
  SavedSearchesResponse,
  WatchlistCompanyAddRequest,
  WatchlistCreateRequest,
  WatchlistDetailResponse,
  WatchlistFeedResponse,
  WatchlistMutationResponse,
  WatchlistQueryAddRequest,
  WatchlistSeenResponse,
  WatchlistUpdateRequest,
  WatchlistsResponse,
  WorkflowActionCatalogResponse,
  WorkflowActionCreateResponse,
  WorkflowActionRequestBody,
  WorkflowArtifactMutationResponse,
  WorkflowCanonical,
  WorkflowDetail,
  WorkflowDetailResponse,
  WorkflowPublishResponse,
  WorkflowRunCancelResponse,
  WorkflowRunCreateResponse,
  WorkflowRunDetailResponse,
  WorkflowRunsResponse,
  WorkflowSaveRequest,
  WorkflowSummary,
  WorkflowsResponse,
  WorkflowToolsResponse,
} from "./types";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    // Grammar-validator error strings from a 422 invalid_definition body —
    // the workflow builder surfaces them verbatim next to its own checks.
    public readonly errors: string[] = [],
  ) {
    super(message);
  }
}

export const loginNavigation = {
  assign(url: string) {
    window.location.assign(url);
  },
};

export async function fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: "same-origin",
    ...init,
    headers: {
      Accept: "application/json",
      // Same-origin proof for the BFF's CSRF check on unsafe methods
      // (harmless on GETs; required alongside SameSite=Lax cookies).
      "X-Requested-With": "fetch",
      ...(init?.headers ?? {}),
    },
  });
  if (response.status === 401) {
    // No portal session: hand off to the existing Entra login flow, which
    // returns here after the cookie is set (same origin, shared cookie).
    loginNavigation.assign(
      `/dashboard/login?next=${encodeURIComponent(window.location.pathname)}`,
    );
    throw new ApiError(401, "redirecting to sign-in");
  }
  if (!response.ok) {
    // BFF errors are JSON {error, message, errors?}; fall back to the status
    // line when the body is not parseable.
    let message = `${path} -> ${response.status}`;
    let errors: string[] = [];
    try {
      const body = (await response.json()) as {
        message?: string;
        errors?: string[];
      };
      if (body?.message) message = body.message;
      if (Array.isArray(body?.errors)) errors = body.errors.map(String);
    } catch {
      // keep fallbacks
    }
    throw new ApiError(response.status, message, errors);
  }
  return (await response.json()) as T;
}

// POST with a JSON body; inherits fetchJson's session/CSRF headers. The
// X-Requested-With header there is what satisfies the BFF's same-origin
// check on unsafe methods.
function postJson<T>(path: string, body: unknown): Promise<T> {
  return fetchJson<T>(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

function patchJson<T>(path: string, body: unknown): Promise<T> {
  return fetchJson<T>(path, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

function putJson<T>(path: string, body: unknown): Promise<T> {
  return fetchJson<T>(path, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

function deleteJson<T>(path: string): Promise<T> {
  return fetchJson<T>(path, { method: "DELETE" });
}

// DEV ONLY: local Vite previews may run without the BFF. In dev builds a
// failed fetch falls back to fixtures so the shell is visually verifiable;
// production builds never use fixtures.
const DEV_SESSION: PortalSession = {
  email: "dev@localhost",
  name: "Dev Preview",
  capabilities: [
    "research.read",
    "research.write",
    "research.export",
    "workflows.read",
    "workflows.write",
    "workflows.run",
    "workflows.approve",
    "watchlists.write",
    "portfolio.read",
    "portfolio.write",
    "operations.read",
    "admin",
  ],
};

const DEV_HOME: HomePayload = {
  headline:
    "20 portfolio · 214 bench · 5,012 universe — 3 items need attention, everything else is quiet.",
  attention: [
    {
      id: "1",
      kind: "monitor_tripped",
      title: "TTD — thesis-killer monitor tripped",
      detail:
        "Amazon DSP pricing pressure: two sell-side notes this week contradict the take-rate stability assumption.",
      when: new Date(Date.now() - 3600e3).toISOString(),
      chip: { label: "monitor", tone: "red" },
      deep_link: "/app/companies/TTD",
    },
    {
      id: "2",
      kind: "memo_published",
      title: "NVDA — earnings pulse memo published",
      detail: "Q2 transcript signals extracted; margin guidance unchanged, DC revenue ahead.",
      when: new Date(Date.now() - 7200e3).toISOString(),
      chip: { label: "memo", tone: "green" },
      deep_link: "/app/research",
    },
    {
      id: "3",
      kind: "gap_closed",
      title: "SAN.MC — evidence gap closed",
      detail: "Restored GB annual report (350pp) now searchable; coverage bars updated.",
      when: new Date(Date.now() - 26 * 3600e3).toISOString(),
      chip: { label: "coverage", tone: "blue" },
      deep_link: "/app/companies/SAN.MC",
    },
  ],
  decisions: [
    {
      id: "d1",
      kind: "decision_needed",
      title: "Approve: re-underwrite run for TTD",
      detail: "Workflow paused at approval step.",
      when: new Date(Date.now() - 1800e3).toISOString(),
      chip: { label: "approval", tone: "amber" },
      deep_link: "/app/approvals/demo",
    },
  ],
  in_flight: [
    {
      id: "f1",
      kind: "run",
      title: "IC Review — MU (step 4/9)",
      detail: "Evidence pack assembled; drafting bear case.",
      when: new Date().toISOString(),
      chip: { label: "running", tone: "blue" },
      deep_link: "/app/agents",
    },
  ],
  latest_portfolio_documents: {
    status: "ok",
    documents: [
      {
        document_id: "6f9d2b1a-0000-4000-8000-000000000001",
        ticker: "TTD",
        company_name: "The Trade Desk",
        title: "Navigating Macro Headwinds — Ad Agency Survey",
        source_name: "Citi Research",
        document_type: "Broker note",
        document_date: "2026-08-08",
        ingested_at: new Date(Date.now() - 45 * 60e3).toISOString(),
        deep_link: "/app/documents/6f9d2b1a-0000-4000-8000-000000000001",
      },
      {
        document_id: "home-doc-mu-earnings",
        ticker: "MU",
        company_name: "Micron Technology",
        title: "Fiscal Q3 earnings call transcript",
        source_name: "Company filings",
        document_type: "Earnings transcript",
        document_date: "2026-08-07",
        ingested_at: new Date(Date.now() - 2 * 3600e3).toISOString(),
        deep_link: "/app/documents/home-doc-mu-earnings",
      },
      {
        document_id: "home-doc-nvda-supply",
        ticker: "NVDA",
        company_name: "NVIDIA",
        title: "AI infrastructure: supply-chain channel check",
        source_name: "UBS Research",
        document_type: "Broker note",
        document_date: "2026-08-07",
        ingested_at: new Date(Date.now() - 4 * 3600e3).toISOString(),
        deep_link: "/app/documents/home-doc-nvda-supply",
      },
      {
        document_id: "home-doc-meta-reels",
        ticker: "META",
        company_name: "Meta Platforms",
        title: "Reels monetization and engagement update",
        source_name: "Morgan Stanley",
        document_type: "Industry research",
        document_date: "2026-08-06",
        ingested_at: new Date(Date.now() - 8 * 3600e3).toISOString(),
        deep_link: "/app/documents/home-doc-meta-reels",
      },
      {
        document_id: "home-doc-san-results",
        ticker: "SAN.MC",
        company_name: "Banco Santander",
        title: "Half-year results and capital update",
        source_name: "Company filings",
        document_type: "Results presentation",
        document_date: "2026-08-06",
        ingested_at: new Date(Date.now() - 12 * 3600e3).toISOString(),
        deep_link: "/app/documents/home-doc-san-results",
      },
      {
        document_id: "home-doc-mcd-traffic",
        ticker: "MCD",
        company_name: "McDonald's",
        title: "US restaurant traffic tracker — July",
        source_name: "JPMorgan Research",
        document_type: "Alternative data",
        document_date: "2026-08-05",
        ingested_at: new Date(Date.now() - 28 * 3600e3).toISOString(),
        deep_link: "/app/documents/home-doc-mcd-traffic",
      },
    ],
  },
};

async function withDevFallback<T>(real: () => Promise<T>, fixture: T): Promise<T> {
  if (!import.meta.env.DEV) {
    return real();
  }
  try {
    return await real();
  } catch (error) {
    // Query cancellation is control flow, not a missing local BFF. Returning
    // a fixture for an aborted autocomplete would let a stale result win.
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    return fixture;
  }
}

export function fetchSession(): Promise<PortalSession> {
  return withDevFallback(() => fetchJson<PortalSession>("/api/portal/session"), DEV_SESSION);
}

export function fetchHome(): Promise<HomePayload> {
  return withDevFallback(() => fetchJson<HomePayload>("/api/portal/home"), DEV_HOME);
}

// --- Saved searches + digests -------------------------------------------------

const DEV_SAVED_SEARCH: SavedSearch = {
  saved_search_id: "5a4b3c2d-0000-4000-8000-000000000001",
  user_email: "dev@localhost",
  name: "TTD competitive threats",
  query: "amazon dsp pricing pressure",
  ticker: "TTD",
  source: null,
  doc_type: null,
  filters: { retrieval_mode: "hybrid" },
  pinned: false,
  digest_enabled: true,
  digest_cadence: "daily",
  created_at: "2026-07-10T08:00:00+00:00",
  updated_at: "2026-07-12T08:00:00+00:00",
  digest_cursor_ingested_at: "2026-07-12T06:00:00+00:00",
  digest_last_checked_at: "2026-07-12T06:00:00+00:00",
  digest_last_notified_at: "2026-07-11T06:00:00+00:00",
};

const DEV_DIGEST_RUNS: DigestRunsResponse = {
  status: "ok",
  runs: [
    {
      digest_run_id: "7c6d5e4f-0000-4000-8000-000000000001",
      saved_search_id: DEV_SAVED_SEARCH.saved_search_id,
      saved_search_name: DEV_SAVED_SEARCH.name,
      period_key: "2026-07-11",
      status: "notified",
      executed_at: "2026-07-11T06:00:00+00:00",
      new_result_count: 3,
      window_from: "2026-07-10T06:00:00+00:00",
      window_to: "2026-07-11T05:00:00+00:00",
      notification_id: "n-1",
      error: null,
      created_at: "2026-07-11T06:00:00+00:00",
    },
    {
      digest_run_id: "7c6d5e4f-0000-4000-8000-000000000002",
      saved_search_id: DEV_SAVED_SEARCH.saved_search_id,
      saved_search_name: DEV_SAVED_SEARCH.name,
      period_key: "2026-07-12",
      status: "skipped_empty",
      executed_at: "2026-07-12T06:00:00+00:00",
      new_result_count: 0,
      window_from: "2026-07-11T05:00:00+00:00",
      window_to: "2026-07-12T05:00:00+00:00",
      notification_id: null,
      error: null,
      created_at: "2026-07-12T06:00:00+00:00",
    },
  ],
  count: 2,
};

const DEV_DIGEST_PREVIEW: DigestPreviewResponse = {
  status: "ok",
  saved_search_id: DEV_SAVED_SEARCH.saved_search_id,
  name: DEV_SAVED_SEARCH.name,
  window: { from: "2026-07-12T06:00:00+00:00", to: new Date().toISOString() },
  cursor_active: true,
  would_send: true,
  result_count: 2,
  results: [
    {
      document_id: "6f9d2b1a-0000-4000-8000-000000000001",
      title: "Navigating Macro Headwinds — Ad Agency Survey",
      source_name: "citi",
      provider: "citi",
      ticker: "TTD",
      doc_date: "2026-05-08",
      page_start: 8,
      deep_link: "/app/documents/6f9d2b1a-0000-4000-8000-000000000001?page=8",
      ingested_at: "2026-07-12T09:00:00+00:00",
    },
    {
      document_id: "6f9d2b1a-0000-4000-8000-000000000002",
      title: "Form 10-Q — Q1 2026",
      source_name: "sec",
      provider: "sec",
      ticker: "TTD",
      doc_date: "2026-05-09",
      page_start: 31,
      deep_link: "/app/documents/6f9d2b1a-0000-4000-8000-000000000002?page=31",
      ingested_at: "2026-07-12T11:00:00+00:00",
    },
  ],
  preview_md: "# TTD competitive threats\n2 new documents",
};

export function fetchSavedSearches(): Promise<SavedSearchesResponse> {
  return withDevFallback(
    () => fetchJson<SavedSearchesResponse>("/api/portal/saved-searches"),
    { status: "ok", saved_searches: [DEV_SAVED_SEARCH], count: 1 },
  );
}

export function updateSavedSearch(
  savedSearchId: string,
  patch: Partial<SavedSearchCreateRequest>,
): Promise<SavedSearchResponse> {
  return withDevFallback(
    () =>
      patchJson<SavedSearchResponse>(`/api/portal/saved-searches/${savedSearchId}`, patch),
    { status: "ok", saved_search: { ...DEV_SAVED_SEARCH, ...patch } as SavedSearch },
  );
}

export function deleteSavedSearch(
  savedSearchId: string,
): Promise<{ status: "ok"; deleted: string }> {
  return withDevFallback(
    () =>
      deleteJson<{ status: "ok"; deleted: string }>(
        `/api/portal/saved-searches/${savedSearchId}`,
      ),
    { status: "ok", deleted: savedSearchId },
  );
}

export function fetchDigestRuns(): Promise<DigestRunsResponse> {
  return withDevFallback(
    () => fetchJson<DigestRunsResponse>("/api/portal/digests"),
    DEV_DIGEST_RUNS,
  );
}

export function previewDigest(savedSearchId: string): Promise<DigestPreviewResponse> {
  return withDevFallback(
    () =>
      postJson<DigestPreviewResponse>(
        `/api/portal/saved-searches/${savedSearchId}/digest/preview`,
        {},
      ),
    DEV_DIGEST_PREVIEW,
  );
}

// --- Watchlists (new-documents feed) -----------------------------------------

const DEV_WATCHLIST_ID = "4d3c2b1a-0000-4000-8000-000000000001";

const DEV_WATCHLISTS: WatchlistsResponse = {
  status: "ok",
  watchlists: [
    {
      watchlist_id: DEV_WATCHLIST_ID,
      name: "Adtech core",
      description: "DSPs and the open-internet supply chain.",
      company_count: 4,
      query_count: 1,
      unseen_count: 2,
      seen_through_ingested_at: "2026-07-10T06:00:00+00:00",
    },
    {
      watchlist_id: "4d3c2b1a-0000-4000-8000-000000000002",
      name: "EU banks",
      description: null,
      company_count: 6,
      query_count: 0,
      unseen_count: 0,
      seen_through_ingested_at: "2026-07-12T06:00:00+00:00",
    },
  ],
  count: 2,
};

const DEV_WATCHLIST_FEED: WatchlistFeedResponse = {
  status: "ok",
  watchlist_id: DEV_WATCHLIST_ID,
  watchlist_name: "Adtech core",
  seen_through_ingested_at: "2026-07-10T06:00:00+00:00",
  since: "2026-07-10T06:00:00+00:00",
  new_count: 2,
  count: 2,
  documents: [
    {
      document_id: "6f9d2b1a-0000-4000-8000-000000000001",
      title: "Navigating Macro Headwinds — Ad Agency Survey",
      source_name: "citi",
      source_type: "institutional_research",
      ticker: "TTD",
      doc_date: "2026-05-08",
      ingested_at: "2026-07-12 09:00:00+00",
      page_count: 24,
      deep_link: "/app/documents/6f9d2b1a-0000-4000-8000-000000000001",
    },
    {
      document_id: "6f9d2b1a-0000-4000-8000-000000000002",
      title: "Form 10-Q — Q1 2026",
      source_name: "sec",
      source_type: "regulatory_filing",
      ticker: "TTD",
      doc_date: "2026-05-09",
      ingested_at: "2026-07-11 11:00:00+00",
      page_count: 58,
      deep_link: "/app/documents/6f9d2b1a-0000-4000-8000-000000000002",
    },
  ],
  next_cursor: null,
};

function devWatchlistDetail(watchlistId: string): WatchlistDetailResponse {
  const summary = DEV_WATCHLISTS.watchlists.find(
    (watchlist) => watchlist.watchlist_id === watchlistId,
  );
  return {
    status: "ok",
    watchlist: {
      watchlist_id: watchlistId,
      name: summary?.name ?? "Watchlist",
      slug: (summary?.name ?? "watchlist").toLowerCase().replaceAll(/[^a-z0-9]+/g, "-"),
      watchlist_type: "mixed",
      description: summary?.description ?? null,
      status: "active",
      priority: "high",
      default_cadence_days: 7,
      default_portal_budget: 20,
    },
    companies: [
      {
        watchlist_company_id: "5e4d3c2b-0000-4000-8000-000000000001",
        ticker: "TTD",
        company_name: "The Trade Desk",
        priority: "high",
        cadence_days: 3,
        effective_cadence_days: 3,
        keywords: ["advertising", "DSP"],
        status: "active",
      },
    ],
    queries: [
      {
        watchlist_query_id: "5e4d3c2b-0000-4000-8000-000000000002",
        query: "Open internet advertising share",
        query_type: "theme",
        description: null,
        related_tickers: ["TTD"],
        keywords: ["programmatic advertising"],
        portals: ["citi", "ubs"],
        priority: "normal",
        cadence_days: 7,
        effective_cadence_days: 7,
        status: "active",
      },
    ],
    company_count: 1,
    query_count: 1,
  };
}

export function fetchWatchlists(): Promise<WatchlistsResponse> {
  return withDevFallback(
    () => fetchJson<WatchlistsResponse>("/api/portal/watchlists"),
    DEV_WATCHLISTS,
  );
}

export function fetchWatchlist(watchlistId: string): Promise<WatchlistDetailResponse> {
  return withDevFallback(
    () => fetchJson<WatchlistDetailResponse>(`/api/portal/watchlists/${watchlistId}`),
    devWatchlistDetail(watchlistId),
  );
}

// First page without a cursor; "Load more" passes the opaque signed cursor.
export function fetchWatchlistNewDocuments(
  watchlistId: string,
  cursor?: string | null,
): Promise<WatchlistFeedResponse> {
  const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : "";
  return withDevFallback(
    () =>
      fetchJson<WatchlistFeedResponse>(
        `/api/portal/watchlists/${watchlistId}/new-documents${query}`,
      ),
    DEV_WATCHLIST_FEED,
  );
}

// Marks the feed seen through the given watermark (a server-issued
// ingested_at echoed back), or through now() when omitted.
export function markWatchlistSeen(
  watchlistId: string,
  ingestedAt?: string | null,
): Promise<WatchlistSeenResponse> {
  return withDevFallback(
    () =>
      postJson<WatchlistSeenResponse>(
        `/api/portal/watchlists/${watchlistId}/seen`,
        ingestedAt ? { ingested_at: ingestedAt } : {},
      ),
    {
      status: "ok",
      watchlist_id: watchlistId,
      seen_through_ingested_at: new Date().toISOString(),
    },
  );
}

// --- Watchlist CRUD (Wave 2.2) ----------------------------------------------

export function createWatchlist(request: WatchlistCreateRequest): Promise<WatchlistMutationResponse> {
  return postJson<WatchlistMutationResponse>("/api/portal/watchlists", request);
}

export function updateWatchlist(
  watchlistId: string,
  request: WatchlistUpdateRequest,
): Promise<WatchlistMutationResponse> {
  return patchJson<WatchlistMutationResponse>(`/api/portal/watchlists/${watchlistId}`, request);
}

export function archiveWatchlist(watchlistId: string): Promise<WatchlistMutationResponse> {
  return deleteJson<WatchlistMutationResponse>(`/api/portal/watchlists/${watchlistId}`);
}

export function addWatchlistCompany(
  watchlistId: string,
  request: WatchlistCompanyAddRequest,
): Promise<WatchlistMutationResponse> {
  return postJson<WatchlistMutationResponse>(
    `/api/portal/watchlists/${watchlistId}/companies`,
    request,
  );
}

export function removeWatchlistCompany(
  watchlistId: string,
  companyId: string,
): Promise<WatchlistMutationResponse> {
  return deleteJson<WatchlistMutationResponse>(
    `/api/portal/watchlists/${watchlistId}/companies/${companyId}`,
  );
}

export function addWatchlistQuery(
  watchlistId: string,
  request: WatchlistQueryAddRequest,
): Promise<WatchlistMutationResponse> {
  return postJson<WatchlistMutationResponse>(
    `/api/portal/watchlists/${watchlistId}/queries`,
    request,
  );
}

export function removeWatchlistQuery(
  watchlistId: string,
  queryId: string,
): Promise<WatchlistMutationResponse> {
  return deleteJson<WatchlistMutationResponse>(
    `/api/portal/watchlists/${watchlistId}/queries/${queryId}`,
  );
}

// --- Standing monitors (Wave 2.3; contract routes keep the agent-* names) ----

const DEV_MONITOR_ID = "7a6b5c4d-0000-4000-8000-000000000001";
const DEV_FCX_MONITOR_ID = "7a6b5c4d-0000-4000-8000-000000000002";
const DEV_MU_MONITOR_ID = "7a6b5c4d-0000-4000-8000-000000000003";
const DEV_NVDA_MONITOR_ID = "7a6b5c4d-0000-4000-8000-000000000004";
const DEV_MCD_MONITOR_ID = "7a6b5c4d-0000-4000-8000-000000000005";
const DEV_ARCHIVED_MONITOR_ID = "7a6b5c4d-0000-4000-8000-000000000006";

let DEV_MONITOR_ROWS: MonitorSummary[] = [
  {
    agent_definition_id: DEV_MONITOR_ID,
    name: "Amazon DSP displaces TTD off-Amazon",
    agent_kind: "thesis_killer",
    state: "tripped",
    signal_state: "tripped",
    operational_status: "ready",
    has_evaluated: true,
    state_summary:
      "Two independent agency surveys report direct displacement in off-Amazon budgets.",
    trigger_type: "event_new_documents",
    last_evaluated_at: "2026-08-08T14:22:00+00:00",
    last_attempt_at: "2026-08-08T14:22:00+00:00",
    last_attempt_outcome: "evaluated",
    owner: "dev@localhost",
    instructions:
      "Escalate when at least two independent sources show Amazon DSP directly displacing The Trade Desk in off-Amazon budgets; distinguish displacement from coexistence.",
    condition: {},
    ticker: "TTD",
    company_name: "The Trade Desk",
    eval_count: 14,
    status: "active",
    updated_at: "2026-08-08T14:22:00+00:00",
    rule_updated_at: "2026-07-12T10:00:00+00:00",
    current_rule_evaluated_at: "2026-08-08T14:22:00+00:00",
    current_rule_evaluated: true,
    rule_needs_evaluation: false,
    cadence_tier: "portfolio",
    cadence_floor_hours: 4,
    next_cadence_eligible_at: "2026-08-08T18:22:00+00:00",
    last_evaluation: {
      state: "tripped",
      confidence: 0.82,
      summary: "Two independent agency surveys report direct displacement in off-Amazon budgets.",
      evaluated_at: "2026-08-08T14:22:00+00:00",
      evidence_count: 2,
      applies_to_current_rule: true,
    },
  },
  {
    agent_definition_id: DEV_FCX_MONITOR_ID,
    name: "Copper demand stagnates",
    agent_kind: "thesis_killer",
    state: "watching",
    signal_state: "watching",
    operational_status: "error",
    has_evaluated: true,
    state_summary: "One source points to weaker construction demand; independent confirmation is missing.",
    trigger_type: "event_new_documents",
    last_evaluated_at: "2026-08-07T09:30:00+00:00",
    last_attempt_at: "2026-08-08T13:05:00+00:00",
    last_attempt_outcome: "failed",
    last_error_at: "2026-08-08T13:05:00+00:00",
    last_error_summary: "The latest check could not be completed.",
    owner: "dev@localhost",
    instructions:
      "Escalate when at least three independent sources in a rolling 30-day period support stagnating or declining global copper demand.",
    condition: {},
    ticker: "FCX",
    company_name: "Freeport-McMoRan",
    eval_count: 9,
    status: "active",
    updated_at: "2026-08-08T13:05:00+00:00",
    last_evaluation: {
      state: "watching",
      confidence: 0.58,
      summary: "One source points to weaker construction demand; independent confirmation is missing.",
      evaluated_at: "2026-08-07T09:30:00+00:00",
      evidence_count: 1,
    },
  },
  {
    agent_definition_id: DEV_MU_MONITOR_ID,
    name: "Revenue growth stays above 10%",
    agent_kind: "key_signal",
    state: "ok",
    signal_state: "ok",
    operational_status: "waiting",
    has_evaluated: true,
    state_summary: "Quarterly revenue growth of 11.8% remains above the 10% threshold.",
    trigger_type: "facts_refresh",
    last_evaluated_at: "2026-08-08T11:40:00+00:00",
    last_attempt_at: "2026-08-08T11:40:00+00:00",
    last_attempt_outcome: "no_fact",
    owner: "dev@localhost",
    instructions: null,
    condition: {
      fact_name: "fmp.income_statement.revenueGrowth",
      operator: ">=",
      threshold: 0.1,
      period_type: "quarterly",
    },
    ticker: "MU",
    company_name: "Micron Technology",
    eval_count: 6,
    status: "active",
    updated_at: "2026-08-08T11:40:00+00:00",
    last_evaluation: {
      state: "ok",
      confidence: 1,
      summary: "Quarterly revenue growth of 11.8% remains above the 10% threshold.",
      evaluated_at: "2026-08-08T11:40:00+00:00",
      evidence_count: 1,
    },
  },
  {
    agent_definition_id: DEV_NVDA_MONITOR_ID,
    name: "Blackwell demand converts to revenue",
    agent_kind: "thesis_killer",
    state: "ok",
    signal_state: null,
    operational_status: "untested",
    has_evaluated: false,
    state_summary: null,
    trigger_type: "event_new_documents",
    last_evaluated_at: null,
    last_attempt_at: null,
    last_attempt_outcome: null,
    owner: "dev@localhost",
    instructions:
      "Escalate if customer or supply-chain evidence shows sustained Blackwell demand is not converting into recognized revenue.",
    condition: {},
    ticker: "NVDA",
    company_name: "NVIDIA",
    eval_count: 0,
    status: "active",
    updated_at: "2026-08-08T10:15:00+00:00",
    last_evaluation: null,
  },
  {
    agent_definition_id: DEV_MCD_MONITOR_ID,
    name: "US traffic remains positive",
    agent_kind: "key_signal",
    state: "paused",
    signal_state: "tripped",
    operational_status: "paused",
    has_evaluated: true,
    state_summary: "Latest reported US traffic fell below zero.",
    trigger_type: "facts_refresh",
    last_evaluated_at: "2026-08-06T16:10:00+00:00",
    last_attempt_at: "2026-08-06T16:10:00+00:00",
    last_attempt_outcome: "evaluated",
    owner: "dev@localhost",
    instructions: null,
    condition: {
      fact_name: "operating.us_same_store_traffic_growth",
      operator: ">",
      threshold: 0,
      period_type: "quarterly",
    },
    ticker: "MCD",
    company_name: "McDonald's",
    eval_count: 4,
    status: "active",
    updated_at: "2026-08-07T08:00:00+00:00",
    last_evaluation: {
      state: "tripped",
      confidence: 1,
      summary: "Latest reported US traffic fell below zero.",
      evaluated_at: "2026-08-06T16:10:00+00:00",
      evidence_count: 1,
    },
  },
  {
    agent_definition_id: DEV_ARCHIVED_MONITOR_ID,
    name: "Wholesale channel inventory rises",
    agent_kind: "thesis_killer",
    state: "watching",
    signal_state: "watching",
    operational_status: "archived",
    has_evaluated: true,
    state_summary: "Channel checks showed elevated inventory before coverage was retired.",
    trigger_type: "event_new_documents",
    last_evaluated_at: "2026-07-18T12:00:00+00:00",
    last_attempt_at: "2026-07-18T12:00:00+00:00",
    last_attempt_outcome: "evaluated",
    owner: "dev@localhost",
    instructions:
      "Escalate if wholesale inventory remains materially above sell-through for two consecutive channel checks.",
    condition: {},
    ticker: "DECK",
    company_name: "Deckers Outdoor",
    eval_count: 11,
    status: "archived",
    updated_at: "2026-07-22T09:00:00+00:00",
    last_evaluation: {
      state: "watching",
      confidence: 0.64,
      summary: "Channel checks showed elevated inventory before coverage was retired.",
      evaluated_at: "2026-07-18T12:00:00+00:00",
      evidence_count: 1,
    },
  },
];

const DEV_MONITOR_EVALUATIONS = new Map<string, MonitorEvaluationsResponse["evaluations"]>([
  [
    DEV_MONITOR_ID,
    [
      {
        agent_evaluation_id: "8b7c6d5e-0000-4000-8000-000000000001",
        evaluated_at: "2026-08-08T14:22:00+00:00",
        state: "tripped",
        confidence: 0.82,
        applies_to_current_rule: true,
        summary: "Two independent agency surveys report direct displacement in off-Amazon budgets.",
        evidence: [
          {
            type: "document_excerpt",
            document_id: "6f9d2b1a-0000-4000-8000-000000000001",
            page: 8,
            title: "Navigating Macro Headwinds — Ad Agency Survey",
            source: "Citi Research",
            date: "2026-08-08",
            deep_link: "/app/documents/6f9d2b1a-0000-4000-8000-000000000001?page=8",
          },
          {
            type: "document_excerpt",
            document_id: "6f9d2b1a-0000-4000-8000-000000000011",
            page: 4,
            title: "Agency DSP Allocation Tracker",
            source: "Morgan Stanley Research",
            date: "2026-08-07",
            deep_link: "/app/documents/6f9d2b1a-0000-4000-8000-000000000011?page=4",
          },
        ],
      },
      {
        agent_evaluation_id: "8b7c6d5e-0000-4000-8000-000000000002",
        evaluated_at: "2026-08-02T22:00:00+00:00",
        state: "watching",
        confidence: 0.55,
        applies_to_current_rule: false,
        summary: "One note flags pricing pressure; displacement was not yet independently corroborated.",
        evidence: [
          {
            type: "document_excerpt",
            document_id: "6f9d2b1a-0000-4000-8000-000000000012",
            page: 12,
            title: "Adtech Pricing and Channel Checks",
            source: "UBS Research",
            date: "2026-08-02",
            deep_link: "/app/documents/6f9d2b1a-0000-4000-8000-000000000012?page=12",
          },
        ],
      },
    ],
  ],
  [
    DEV_FCX_MONITOR_ID,
    [
      {
        agent_evaluation_id: "8b7c6d5e-0000-4000-8000-000000000021",
        evaluated_at: "2026-08-07T09:30:00+00:00",
        state: "watching",
        confidence: 0.58,
        applies_to_current_rule: true,
        summary: "One source points to weaker construction demand; independent confirmation is missing.",
        evidence: [
          {
            type: "document_excerpt",
            document_id: "6f9d2b1a-0000-4000-8000-000000000021",
            page: 6,
            title: "Global Metals Demand Monitor",
            source: "JPMorgan Research",
            date: "2026-08-07",
            deep_link: "/app/documents/6f9d2b1a-0000-4000-8000-000000000021?page=6",
          },
        ],
      },
    ],
  ],
  [
    DEV_MU_MONITOR_ID,
    [
      {
        agent_evaluation_id: "8b7c6d5e-0000-4000-8000-000000000031",
        evaluated_at: "2026-08-08T11:40:00+00:00",
        state: "ok",
        confidence: 1,
        applies_to_current_rule: true,
        summary: "Quarterly revenue growth of 11.8% remains above the 10% threshold.",
        evidence: [
          {
            type: "fact_observation",
            fact_name: "fmp.income_statement.revenueGrowth",
            value: 0.118,
            period_end: "2026-05-31",
          },
        ],
      },
    ],
  ],
  [
    DEV_MCD_MONITOR_ID,
    [
      {
        agent_evaluation_id: "8b7c6d5e-0000-4000-8000-000000000051",
        evaluated_at: "2026-08-06T16:10:00+00:00",
        state: "tripped",
        confidence: 1,
        applies_to_current_rule: true,
        summary: "Latest reported US traffic fell below zero.",
        evidence: [
          {
            type: "fact_observation",
            fact_name: "operating.us_same_store_traffic_growth",
            value: -0.012,
            period_end: "2026-06-30",
          },
        ],
      },
    ],
  ],
  [
    DEV_ARCHIVED_MONITOR_ID,
    [
      {
        agent_evaluation_id: "8b7c6d5e-0000-4000-8000-000000000061",
        evaluated_at: "2026-07-18T12:00:00+00:00",
        state: "watching",
        confidence: 0.64,
        applies_to_current_rule: true,
        summary: "Channel checks showed elevated inventory before coverage was retired.",
        evidence: [
          {
            type: "document_excerpt",
            document_id: "6f9d2b1a-0000-4000-8000-000000000061",
            page: 3,
            title: "Footwear Wholesale Channel Check",
            source: "Citi Research",
            date: "2026-07-18",
            deep_link: "/app/documents/6f9d2b1a-0000-4000-8000-000000000061?page=3",
          },
        ],
      },
    ],
  ],
]);

function monitorSignal(monitor: MonitorSummary): "ok" | "watching" | "tripped" | null {
  if (monitor.rule_needs_evaluation || monitor.current_rule_evaluated === false) return null;
  if (monitor.signal_state !== undefined) return monitor.signal_state;
  if (monitor.last_evaluation?.state) return monitor.last_evaluation.state;
  return monitor.state === "paused" ? null : monitor.state;
}

function monitorOperationalStatus(monitor: MonitorSummary): string {
  if (monitor.status === "archived") return "archived";
  if (monitor.state === "paused") return "paused";
  return monitor.operational_status ?? (monitor.last_evaluated_at ? "ready" : "untested");
}

function devActiveOperationalStatus(monitor: MonitorSummary): MonitorSummary["operational_status"] {
  if (monitor.in_progress) return "evaluating";
  if (
    monitor.last_attempt_error
    || monitor.last_attempt_outcome === "failed"
    || monitor.last_attempt_outcome === "configuration_error"
  ) return "error";
  if (monitor.next_evaluation_due_at || monitor.rule_needs_evaluation) return "pending";
  if (
    monitor.last_attempt_outcome === "no_new_documents"
    || monitor.last_attempt_outcome === "no_fact"
    || monitor.last_attempt_outcome === "stale_fact"
  ) {
    return "waiting";
  }
  return monitor.has_evaluated ? "ready" : "untested";
}

function monitorMatchesCommonFilters(monitor: MonitorSummary, request: MonitorListRequest): boolean {
  const query = request.q?.trim().toLowerCase();
  if (
    query
    && ![
      monitor.name,
      monitor.ticker,
      monitor.company_name,
      monitor.state_summary ?? "",
    ].some((value) => value.toLowerCase().includes(query))
  ) {
    return false;
  }
  if (request.kind && monitor.agent_kind !== request.kind) return false;
  if (request.ticker && monitor.ticker.toUpperCase() !== request.ticker.trim().toUpperCase()) {
    return false;
  }
  return true;
}

function devMonitorList(request: MonitorListRequest): MonitorsResponse {
  const common = DEV_MONITOR_ROWS.filter((monitor) => monitorMatchesCommonFilters(monitor, request));
  const active = common.filter((monitor) => monitor.status !== "archived");
  const archived = common.filter((monitor) => monitor.status === "archived");
  const summary = {
    signal_needs_review: active.filter((monitor) => {
      const signal = monitorSignal(monitor);
      return signal === "tripped" || signal === "watching";
    }).length,
    needs_review: active.filter((monitor) => {
      const signal = monitorSignal(monitor);
      return signal === "tripped"
        || signal === "watching"
        || monitorOperationalStatus(monitor) === "error"
        || monitor.rule_needs_evaluation === true
        || monitor.last_attempt_outcome === "no_fact"
        || monitor.last_attempt_outcome === "stale_fact";
    }).length,
    untested: active.filter((monitor) => monitorOperationalStatus(monitor) === "untested").length,
    quiet: active.filter(
      (monitor) =>
        monitorSignal(monitor) === "ok"
        && monitorOperationalStatus(monitor) === "ready",
    ).length,
    paused: active.filter((monitor) => monitorOperationalStatus(monitor) === "paused").length,
    archived: archived.length,
    setup_issues: active.filter((monitor) => monitorOperationalStatus(monitor) === "error").length,
    data_issues: active.filter(
      (monitor) => monitor.last_attempt_outcome === "no_fact" || monitor.last_attempt_outcome === "stale_fact",
    ).length,
    rule_changes_pending: active.filter((monitor) => monitor.rule_needs_evaluation === true).length,
  };
  let filtered = common.filter((monitor) => {
    const status = request.status ?? "active";
    if (status === "active" && monitor.status === "archived") return false;
    if (status === "archived" && monitor.status !== "archived") return false;
    if (request.state === "needs_review") {
      const signal = monitorSignal(monitor);
      return signal === "tripped"
        || signal === "watching"
        || monitorOperationalStatus(monitor) === "error"
        || monitor.rule_needs_evaluation === true
        || monitor.last_attempt_outcome === "no_fact"
        || monitor.last_attempt_outcome === "stale_fact";
    }
    if (request.state === "paused") return monitorOperationalStatus(monitor) === "paused";
    if (request.state === "untested") return monitorOperationalStatus(monitor) === "untested";
    if (
      request.state === "error"
      || request.state === "pending"
      || request.state === "waiting"
      || request.state === "evaluating"
    ) {
      return monitorOperationalStatus(monitor) === request.state;
    }
    if (request.state === "ok") {
      return monitorSignal(monitor) === "ok" && monitorOperationalStatus(monitor) === "ready";
    }
    if (request.state && monitorSignal(monitor) !== request.state) return false;
    return true;
  });
  const signalOrder = { tripped: 0, watching: 1, ok: 3 } as const;
  filtered = filtered.sort((left, right) => {
    const leftSignal = monitorSignal(left);
    const rightSignal = monitorSignal(right);
    const leftRank = leftSignal ? signalOrder[leftSignal] : 2;
    const rightRank = rightSignal ? signalOrder[rightSignal] : 2;
    return leftRank - rightRank || String(right.updated_at).localeCompare(String(left.updated_at));
  });
  const limit = Math.max(1, Math.min(100, request.limit ?? 25));
  const cursorMatch = /^dev-monitor-(\d+)$/.exec(request.cursor ?? "");
  const offset = cursorMatch ? Number(cursorMatch[1]) : 0;
  const monitors = filtered.slice(offset, offset + limit);
  const nextOffset = offset + monitors.length;
  return {
    status: "ok",
    generated_at: new Date().toISOString(),
    monitors,
    count: monitors.length,
    total: filtered.length,
    summary,
    next_cursor: nextOffset < filtered.length ? `dev-monitor-${nextOffset}` : null,
  };
}

function monitorQuery(request: MonitorListRequest): string {
  const query = new URLSearchParams();
  if (request.q?.trim()) query.set("q", request.q.trim());
  if (request.status) query.set("status", request.status);
  if (request.state) query.set("state", request.state);
  if (request.kind) query.set("kind", request.kind);
  if (request.ticker?.trim()) query.set("ticker", request.ticker.trim().toUpperCase());
  if (request.cursor) query.set("cursor", request.cursor);
  query.set("limit", String(Math.max(1, Math.min(100, request.limit ?? 25))));
  return query.toString();
}

async function withMonitorDevMutationFallback<T>(
  real: () => Promise<T>,
  fallback: () => T,
): Promise<T> {
  if (!import.meta.env.DEV) return real();
  try {
    return await real();
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    if (
      error instanceof ApiError
      && ![404, 500, 502, 503, 504].includes(error.status)
    ) {
      throw error;
    }
    return fallback();
  }
}

export function fetchMonitors(
  request: MonitorListRequest = {},
  signal?: AbortSignal,
): Promise<MonitorsResponse> {
  const query = monitorQuery(request);
  return withDevFallback(
    () => fetchJson<MonitorsResponse>(`/api/portal/agents?${query}`, { signal }),
    devMonitorList(request),
  );
}

export function fetchMonitor(agentId: string): Promise<MonitorDetailResponse> {
  const fixture = DEV_MONITOR_ROWS.find((monitor) => monitor.agent_definition_id === agentId);
  const real = () => fetchJson<MonitorDetailResponse>(`/api/portal/agents/${encodeURIComponent(agentId)}`);
  if (fixture) return withDevFallback(real, { status: "ok", monitor: fixture });
  if (!import.meta.env.DEV) return real();
  return real().catch((error: unknown) => {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    if (error instanceof ApiError && ![404, 500, 502, 503, 504].includes(error.status)) throw error;
    throw new ApiError(404, "Monitor not found");
  });
}

export function createMonitor(request: MonitorCreateRequest): Promise<MonitorMutationResponse> {
  return withMonitorDevMutationFallback(
    () => postJson<MonitorMutationResponse>("/api/portal/agents", request),
    () => {
      const now = new Date().toISOString();
      const ticker = request.ticker.toUpperCase();
      const selectedCompany = DEV_COMPANIES.companies.find(
        (company) => company.issuer_id === request.issuer_id && company.ticker?.toUpperCase() === ticker,
      );
      if (!selectedCompany) {
        throw new ApiError(422, "Selected issuer and ticker are not an active company pair");
      }
      const monitor: MonitorSummary = {
        agent_definition_id:
          globalThis.crypto?.randomUUID?.() ?? `dev-monitor-${Date.now().toString(36)}`,
        name: request.name,
        agent_kind: request.agent_kind,
        state: "ok",
        signal_state: null,
        operational_status: "untested",
        has_evaluated: false,
        state_summary: null,
        trigger_type:
          request.agent_kind === "thesis_killer" ? "event_new_documents" : "facts_refresh",
        last_evaluated_at: null,
        last_attempt_at: null,
        last_attempt_outcome: null,
        owner: "dev@localhost",
        instructions: request.instructions ?? null,
        condition: { ...(request.condition ?? {}) },
        ticker,
        company_name: selectedCompany.company_name ?? ticker,
        eval_count: 0,
        status: "active",
        updated_at: now,
        last_evaluation: null,
        rule_updated_at: now,
        current_rule_evaluated_at: null,
        current_rule_evaluated: false,
        rule_needs_evaluation: false,
      };
      DEV_MONITOR_ROWS = [monitor, ...DEV_MONITOR_ROWS];
      DEV_MONITOR_EVALUATIONS.set(monitor.agent_definition_id, []);
      return { status: "ok", monitor };
    },
  );
}

export function updateMonitor(
  agentId: string,
  request: MonitorUpdateRequest,
): Promise<MonitorMutationResponse> {
  return withMonitorDevMutationFallback(
    () => patchJson<MonitorMutationResponse>(`/api/portal/agents/${agentId}`, request),
    () => {
      const index = DEV_MONITOR_ROWS.findIndex((monitor) => monitor.agent_definition_id === agentId);
      if (index < 0) throw new ApiError(404, "Monitor not found");
      const current = DEV_MONITOR_ROWS[index];
      const signal = monitorSignal(current);
      const next: MonitorSummary = {
        ...current,
        ...(request.name !== undefined ? { name: request.name } : {}),
        ...(request.instructions !== undefined ? { instructions: request.instructions } : {}),
        ...(request.trigger_type !== undefined ? { trigger_type: request.trigger_type } : {}),
        ...(request.condition !== undefined ? { condition: { ...request.condition } } : {}),
        ...(request.status !== undefined ? { status: request.status } : {}),
        updated_at: new Date().toISOString(),
      };
      if (request.status === "archived") {
        next.operational_status = "archived";
      } else if (request.status === "active") {
        next.state = signal ?? "ok";
        next.next_evaluation_due_at = next.updated_at;
        next.operational_status = devActiveOperationalStatus(next);
      }
      if (request.paused === true) {
        next.state = "paused";
        next.operational_status = "paused";
      } else if (request.paused === false) {
        next.state = signal ?? "ok";
        next.next_evaluation_due_at = next.updated_at;
        next.operational_status = devActiveOperationalStatus(next);
      }
      if (request.instructions !== undefined || request.condition !== undefined) {
        next.operational_status = "pending";
        next.next_evaluation_due_at = next.updated_at;
        next.rule_updated_at = next.updated_at;
        next.current_rule_evaluated_at = null;
        next.current_rule_evaluated = false;
        next.rule_needs_evaluation = true;
        next.signal_state = null;
        if (next.last_evaluation) {
          next.last_evaluation = { ...next.last_evaluation, applies_to_current_rule: false };
        }
      }
      DEV_MONITOR_ROWS = DEV_MONITOR_ROWS.map((monitor, rowIndex) =>
        rowIndex === index ? next : monitor,
      );
      return { status: "ok", monitor: next };
    },
  );
}

export function fetchMonitorEvaluations(
  agentId: string,
  cursor?: string | null,
  limit = 10,
): Promise<MonitorEvaluationsResponse> {
  const query = new URLSearchParams();
  if (cursor) query.set("cursor", cursor);
  query.set("limit", String(Math.max(1, Math.min(100, limit))));
  const monitor = DEV_MONITOR_ROWS.find((row) => row.agent_definition_id === agentId);
  const allEvaluations = DEV_MONITOR_EVALUATIONS.get(agentId) ?? [];
  const cursorMatch = /^dev-evaluation-(\d+)$/.exec(cursor ?? "");
  const offset = cursorMatch ? Number(cursorMatch[1]) : 0;
  const page = allEvaluations.slice(offset, offset + limit);
  const nextOffset = offset + page.length;
  const fixture: MonitorEvaluationsResponse | null = monitor ? {
    status: "ok",
    monitor: {
      agent_definition_id: monitor.agent_definition_id,
      name: monitor.name,
      agent_kind: monitor.agent_kind,
      state: monitor.state,
      ticker: monitor.ticker,
      company_name: monitor.company_name,
    },
    evaluations: page,
    count: page.length,
    next_cursor: nextOffset < allEvaluations.length ? `dev-evaluation-${nextOffset}` : null,
  } : null;
  const real = () => fetchJson<MonitorEvaluationsResponse>(
    `/api/portal/agents/${encodeURIComponent(agentId)}/evaluations?${query.toString()}`,
  );
  if (fixture) return withDevFallback(real, fixture);
  if (!import.meta.env.DEV) return real();
  return real().catch((error: unknown) => {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    if (error instanceof ApiError && ![404, 500, 502, 503, 504].includes(error.status)) throw error;
    throw new ApiError(404, "Monitor not found");
  });
}

// --- Document viewer ----------------------------------------------------------

const DEV_DOCUMENT_ID = "6f9d2b1a-0000-4000-8000-000000000001";

const DEV_DOCUMENT: DocumentDetailResponse = {
  status: "ok",
  document: {
    document_id: DEV_DOCUMENT_ID,
    external_document_id: "citi_11064252",
    source_type: "institutional_research",
    source_name: "citi",
    provider: "citi",
    ticker: "TTD",
    company_name: "The Trade Desk",
    title: "Navigating Macro Headwinds — Ad Agency Survey",
    doc_date: "2026-05-08",
    published_at: null,
    language: "en",
    source_url: null,
    document_version_id: "9a8b7c6d-0000-4000-8000-000000000001",
    content_type: "application/pdf",
    is_pdf: true,
    page_count: 24,
    extracted_pages: 24,
    chunk_count: 4,
    text_char_count: 88000,
    quality_status: "ok",
    ingested_at: "2026-05-09T01:00:00+00:00",
  },
};

const DEV_CHUNKS: DocumentChunksResponse = {
  status: "ok",
  count: 4,
  chunks: [
    {
      chunk_id: "0c1f6a3e-0000-4000-8000-000000000001",
      chunk_index: 0,
      page_start: 1,
      page_end: 2,
      section_title: "Summary",
      token_count: 180,
      truncated: false,
      snippet_html:
        "We surveyed 40 ad agencies on DSP budget allocation heading into the upfronts.",
    },
    {
      chunk_id: "0c1f6a3e-0000-4000-8000-000000000012",
      chunk_index: 1,
      page_start: 8,
      page_end: 8,
      section_title: "Survey results",
      token_count: 220,
      truncated: false,
      snippet_html:
        "TTD remains the clear leader in DSP budget allocation at 80% usage (up 10pts), while Amazon DSP off-Amazon usage rose to 48%.",
    },
    {
      chunk_id: "0c1f6a3e-0000-4000-8000-000000000013",
      chunk_index: 2,
      page_start: 9,
      page_end: 10,
      section_title: "Pricing pressure",
      token_count: 240,
      truncated: false,
      snippet_html:
        "Take-rate compression risk: two-thirds of respondents expect platform fees to fall over the next 12 months.",
    },
    {
      chunk_id: "0c1f6a3e-0000-4000-8000-000000000014",
      chunk_index: 3,
      page_start: 17,
      page_end: 18,
      section_title: "Valuation",
      token_count: 200,
      truncated: false,
      snippet_html: "We maintain our Buy rating with a $92 target (24x FY27 EBITDA).",
    },
  ],
};

function devDocumentPage(pageNumber: number): DocumentPageResponse {
  const chunk = DEV_CHUNKS.chunks.find(
    (candidate) =>
      candidate.page_start != null &&
      candidate.page_end != null &&
      pageNumber >= candidate.page_start &&
      pageNumber <= candidate.page_end,
  );
  const body = chunk
    ? `${chunk.section_title}\n\n${chunk.snippet_html} Lorem ipsum filler for the dev fixture: page ${pageNumber} of the survey deck, extracted text rendered page-by-page.`
    : `Page ${pageNumber} extracted text (dev fixture). Charts and tables on this page are available in the Figures tab.`;
  return {
    status: "ok",
    page: {
      page_id: `dev-page-${pageNumber}`,
      document_id: DEV_DOCUMENT_ID,
      document_version_id: "9a8b7c6d-0000-4000-8000-000000000001",
      page_number: pageNumber,
      text: body,
      truncated: false,
      text_char_count: body.length,
      language: "en",
      extraction_method: "native",
      ocr_used: false,
      is_boilerplate: false,
      boilerplate_kind: null,
      content_quality_flags: [],
      citation: {
        external_document_id: "citi_11064252",
        source_name: "citi",
        ticker: "TTD",
        title: "Navigating Macro Headwinds — Ad Agency Survey",
        doc_date: "2026-05-08",
        page_number: pageNumber,
      },
    },
  };
}

const DEV_FIGURES: DocumentFiguresResponse = {
  status: "ok",
  count: 2,
  figures: [
    {
      figure_id: "f1a2b3c4-0000-4000-8000-000000000001",
      page_number: 8,
      figure_index: 1,
      figure_type: "chart",
      caption: "DSP budget allocation by platform",
      surrounding_text: "Exhibit 4: agencies were asked to allocate 100 points…",
      truncated: false,
      is_boilerplate: false,
      has_image: true,
      asset_ref: "dev-asset-ref.figure-1",
    },
    {
      figure_id: "f1a2b3c4-0000-4000-8000-000000000002",
      page_number: 11,
      figure_index: 2,
      figure_type: "chart",
      caption: "Expected platform fee direction, next 12 months",
      surrounding_text: "Exhibit 7",
      truncated: false,
      is_boilerplate: false,
      has_image: false,
      asset_ref: null,
    },
  ],
};

const DEV_LINK: DocumentLinkResponse = {
  status: "ok",
  document: {
    document_id: DEV_DOCUMENT_ID,
    external_document_id: "citi_11064252",
    title: "Navigating Macro Headwinds — Ad Agency Survey",
    content_type: "application/pdf",
    is_pdf: true,
    page_count: 24,
  },
  link: {
    url: `${import.meta.env.BASE_URL}document-preview.pdf`,
    expires_in_seconds: 3600,
    expires_at: new Date(Date.now() + 3600e3).toISOString(),
  },
};

// A 1x1 transparent PNG so the dev figure gallery renders without S3.
const DEV_PRESIGN: AssetPresignResponse = {
  status: "ok",
  kind: "figure_image",
  url:
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==",
  content_type: "image/png",
  expires_in_seconds: 300,
  expires_at: new Date(Date.now() + 300e3).toISOString(),
};

export function fetchDocument(documentId: string): Promise<DocumentDetailResponse> {
  return withDevFallback(
    () => fetchJson<DocumentDetailResponse>(`/api/portal/documents/${documentId}`),
    DEV_DOCUMENT,
  );
}

export function fetchDocumentPage(
  documentId: string,
  pageNumber: number,
): Promise<DocumentPageResponse> {
  return withDevFallback(
    () =>
      fetchJson<DocumentPageResponse>(
        `/api/portal/documents/${documentId}/pages/${pageNumber}`,
      ),
    devDocumentPage(pageNumber),
  );
}

export function fetchDocumentChunks(documentId: string): Promise<DocumentChunksResponse> {
  return withDevFallback(
    () => fetchJson<DocumentChunksResponse>(`/api/portal/documents/${documentId}/chunks`),
    DEV_CHUNKS,
  );
}

// Minted when the authorized PDF viewer loads (or when a non-PDF original is
// explicitly opened). The BFF still owns capability and document entitlement.
export function fetchDocumentLink(documentId: string): Promise<DocumentLinkResponse> {
  return withDevFallback(
    () => fetchJson<DocumentLinkResponse>(`/api/portal/documents/${documentId}/link`),
    DEV_LINK,
  );
}

export function fetchDocumentFigures(
  documentId: string,
): Promise<DocumentFiguresResponse> {
  return withDevFallback(
    () => fetchJson<DocumentFiguresResponse>(`/api/portal/documents/${documentId}/figures`),
    DEV_FIGURES,
  );
}

export function presignAsset(assetRef: string): Promise<AssetPresignResponse> {
  return withDevFallback(
    () => postJson<AssetPresignResponse>("/api/portal/assets/presign", { asset_ref: assetRef }),
    DEV_PRESIGN,
  );
}

// --- Workflows (builder + runs) ------------------------------------------------

const DEV_WORKFLOW_ID = "8e7f6a5b-0000-4000-8000-000000000001";
const DEV_RUN_ID = "9f8e7d6c-0000-4000-8000-000000000001";

const DEV_WORKFLOW_CANONICAL: WorkflowCanonical = {
  name: "Coverage sentinel",
  description: "Search new TTD coverage and email me a summary.",
  nodes: [
    { id: "start", type: "trigger.manual", config: {} },
    {
      id: "search",
      type: "agent.tool",
      config: { tool: "research_search", arguments: { query: "TTD take rates", limit: 10 } },
    },
    {
      id: "notify",
      type: "notify.email",
      config: {
        recipient_source: "run.triggered_by",
        subject_template: "Coverage sentinel: {{steps.search.output.count}} hits",
        body_template: "Search finished.",
      },
    },
  ],
  edges: [
    ["start", "search"],
    ["search", "notify"],
  ],
};

const DEV_WORKFLOW: WorkflowDetail = {
  workflow_id: DEV_WORKFLOW_ID,
  name: "Coverage sentinel",
  version: 1,
  status: "draft",
  description: "Search new TTD coverage and email me a summary.",
  grammar_version: 2,
  budget_class: "standard",
  trigger: { node_id: "start", type: "manual" },
  manual_run_supported: true,
  test_run_supported: true,
  created_by: "dev@localhost",
  updated_by: "dev@localhost",
  created_at: "2026-07-12T08:00:00+00:00",
  updated_at: "2026-07-12T09:00:00+00:00",
  last_run: {
    workflow_run_id: DEV_RUN_ID,
    status: "succeeded",
    mode: "dry_run",
    created_at: "2026-07-12T09:30:00+00:00",
    completed_at: "2026-07-12T09:31:00+00:00",
  },
  last_live_run: null,
  last_test_run: {
    workflow_run_id: DEV_RUN_ID,
    status: "succeeded",
    mode: "dry_run",
    created_at: "2026-07-12T09:30:00+00:00",
    completed_at: "2026-07-12T09:31:00+00:00",
  },
  canonical_json: DEV_WORKFLOW_CANONICAL,
  validation: { valid: true, errors: [] },
};

const DEV_LIBRARY_WORKFLOWS: WorkflowSummary[] = [
  {
    workflow_id: "8e7f6a5b-0000-4000-8000-000000000011",
    name: "library: Weekly coverage digest",
    version: 1,
    status: "published",
    description:
      "Prepare a Monday source-coverage digest for the research watchlist.",
    grammar_version: 2,
    budget_class: "light",
    trigger: {
      node_id: "monday",
      type: "schedule",
      cron: "0 6 * * 1",
      timezone: "UTC",
      enabled: false,
      next_fire_at: null,
    },
    manual_run_supported: false,
    test_run_supported: false,
    created_by: "mark@daher.me",
    updated_by: "mark@daher.me",
    created_at: "2026-07-10T08:00:00+00:00",
    updated_at: "2026-07-10T08:00:00+00:00",
    last_run: {
      workflow_run_id: "9f8e7d6c-0000-4000-8000-000000000011",
      status: "succeeded",
      mode: "dry_run",
      created_at: "2026-07-11T06:00:00+00:00",
      completed_at: "2026-07-11T06:01:00+00:00",
    },
    last_live_run: null,
    last_test_run: {
      workflow_run_id: "9f8e7d6c-0000-4000-8000-000000000011",
      status: "succeeded",
      mode: "dry_run",
      created_at: "2026-07-11T06:00:00+00:00",
      completed_at: "2026-07-11T06:01:00+00:00",
    },
  },
  {
    workflow_id: "8e7f6a5b-0000-4000-8000-000000000012",
    name: "library: New filing alert",
    version: 1,
    status: "published",
    description:
      "Review newly ingested filings and notify the research operator with the changes.",
    grammar_version: 2,
    budget_class: "standard",
    trigger: {
      node_id: "filed",
      type: "event",
      event_type: "document_ingested",
      filter: { payload: { source_type: ["filing", "public_exchange", "company_ir"] } },
      enabled: false,
      next_fire_at: null,
    },
    manual_run_supported: false,
    test_run_supported: false,
    created_by: "mark@daher.me",
    updated_by: "mark@daher.me",
    created_at: "2026-07-10T08:00:00+00:00",
    updated_at: "2026-07-10T08:00:00+00:00",
    last_run: {
      workflow_run_id: "9f8e7d6c-0000-4000-8000-000000000012",
      status: "failed",
      mode: "dry_run",
      created_at: "2026-07-11T05:00:00+00:00",
      completed_at: "2026-07-11T05:00:12+00:00",
    },
    last_live_run: null,
    last_test_run: {
      workflow_run_id: "9f8e7d6c-0000-4000-8000-000000000012",
      status: "failed",
      mode: "dry_run",
      created_at: "2026-07-11T05:00:00+00:00",
      completed_at: "2026-07-11T05:00:12+00:00",
    },
  },
];

const DEV_WORKFLOW_TOOLS: WorkflowToolsResponse = {
  status: "ok",
  count: 3,
  tools: [
    {
      name: "research_search",
      description: "Hybrid retrieval over the ingested corpus.",
      input_schema: {
        type: "object",
        properties: {
          query: { type: "string", description: "Search query" },
          ticker: { type: "string" },
          limit: { type: "number" },
          retrieval_mode: { type: "string", enum: ["hybrid", "keyword", "vector"] },
        },
        required: ["query"],
      },
      read_only: true,
    },
    {
      name: "watchlist_add_company",
      description: "Add a company to a watchlist.",
      input_schema: {
        type: "object",
        properties: {
          watchlist: { type: "string" },
          ticker: { type: "string" },
        },
        required: ["watchlist", "ticker"],
      },
      read_only: false,
    },
    {
      name: "intelligence_operator_digest",
      description: "Send the operator digest notification.",
      input_schema: { type: "object", properties: { hours: { type: "number" } } },
      read_only: false,
    },
  ],
};

function devRun(overrides: Partial<WorkflowRunDetailResponse["run"]>): WorkflowRunDetailResponse["run"] {
  return {
    workflow_run_id: DEV_RUN_ID,
    workflow_id: DEV_WORKFLOW_ID,
    definition_name: "Coverage sentinel",
    definition_version: 1,
    mode: "dry_run",
    status: "succeeded",
    outcome: "completed",
    trigger_reason: "manual",
    triggered_by: "dev@localhost",
    current_step_node_id: null,
    failed_step_node_id: null,
    steps_completed: 2,
    steps_total: 2,
    total_input_tokens: 1200,
    total_output_tokens: 480,
    cost_estimate_usd: 0.0132,
    last_error: null,
    created_at: "2026-07-12T09:30:00+00:00",
    started_at: "2026-07-12T09:30:05+00:00",
    completed_at: "2026-07-12T09:31:00+00:00",
    input_payload: { trigger: { triggered_by: "dev@localhost" }, input: {} },
    output_summary: { completed_nodes: ["search", "notify"] },
    canonical_snapshot: DEV_WORKFLOW_CANONICAL,
    workflow_trigger_id: null,
    platform_event_id: null,
    ...overrides,
  };
}

const DEV_RUN_DETAIL: WorkflowRunDetailResponse = {
  status: "ok",
  run: devRun({}),
  steps: [
    {
      workflow_run_step_id: "step-1",
      node_id: "search",
      node_type: "agent.tool",
      step_index: 1,
      attempt: 1,
      status: "succeeded",
      input: { tool: "research_search", arguments: { query: "TTD take rates", limit: 10 } },
      output: { count: 4, results: ["…"] },
      error: null,
      model_run_id: null,
      input_token_count: null,
      output_token_count: null,
      cost_estimate_usd: null,
      started_at: "2026-07-12T09:30:05+00:00",
      completed_at: "2026-07-12T09:30:20+00:00",
    },
    {
      workflow_run_step_id: "step-2",
      node_id: "notify",
      node_type: "notify.email",
      step_index: 2,
      attempt: 1,
      status: "succeeded",
      input: { recipients: ["dev@localhost"], subject: "Coverage sentinel: 4 hits" },
      output: { dry_run: true, would_notify: ["dev@localhost"] },
      error: null,
      model_run_id: null,
      input_token_count: null,
      output_token_count: null,
      cost_estimate_usd: null,
      started_at: "2026-07-12T09:30:21+00:00",
      completed_at: "2026-07-12T09:30:22+00:00",
    },
  ],
  approval: null,
};

export function fetchWorkflowTools(): Promise<WorkflowToolsResponse> {
  return withDevFallback(
    () => fetchJson<WorkflowToolsResponse>("/api/portal/workflow-tools"),
    DEV_WORKFLOW_TOOLS,
  );
}

export function fetchWorkflows(status?: string): Promise<WorkflowsResponse> {
  const query = status ? `?status=${encodeURIComponent(status)}` : "";
  return withDevFallback(
    () => fetchJson<WorkflowsResponse>(`/api/portal/workflows${query}`),
    {
      status: "ok",
      workflows: [...DEV_LIBRARY_WORKFLOWS, DEV_WORKFLOW],
      count: DEV_LIBRARY_WORKFLOWS.length + 1,
    },
  );
}

export function fetchWorkflow(workflowId: string): Promise<WorkflowDetailResponse> {
  return withDevFallback(
    () => fetchJson<WorkflowDetailResponse>(`/api/portal/workflows/${workflowId}`),
    { status: "ok", workflow: DEV_WORKFLOW },
  );
}

export function createWorkflow(request: WorkflowSaveRequest): Promise<WorkflowDetailResponse> {
  return withDevFallback(
    () => postJson<WorkflowDetailResponse>("/api/portal/workflows", request),
    {
      status: "ok",
      workflow: { ...DEV_WORKFLOW, name: request.definition.name, canonical_json: request.definition },
    },
  );
}

export function updateWorkflow(
  workflowId: string,
  request: WorkflowSaveRequest,
): Promise<WorkflowDetailResponse> {
  return withDevFallback(
    () => putJson<WorkflowDetailResponse>(`/api/portal/workflows/${workflowId}`, request),
    {
      status: "ok",
      workflow: { ...DEV_WORKFLOW, name: request.definition.name, canonical_json: request.definition },
    },
  );
}

export function publishWorkflow(workflowId: string): Promise<WorkflowPublishResponse> {
  return withDevFallback(
    () => postJson<WorkflowPublishResponse>(`/api/portal/workflows/${workflowId}/publish`, {}),
    {
      status: "ok",
      workflow: { ...DEV_WORKFLOW, status: "published" },
      superseded: [],
      triggers_projected: 1,
    },
  );
}

export function archiveWorkflow(workflowId: string): Promise<WorkflowDetailResponse> {
  return withDevFallback(
    () => postJson<WorkflowDetailResponse>(`/api/portal/workflows/${workflowId}/archive`, {}),
    { status: "ok", workflow: { ...DEV_WORKFLOW, status: "archived" } },
  );
}

export function runWorkflow(
  workflowId: string,
  mode: "live" | "dry_run",
  input?: Record<string, unknown>,
): Promise<WorkflowRunCreateResponse> {
  return withDevFallback(
    () =>
      postJson<WorkflowRunCreateResponse>(`/api/portal/workflows/${workflowId}/run`, {
        mode,
        ...(input ? { input } : {}),
      }),
    {
      status: "queued",
      workflow_run_id: DEV_RUN_ID,
      workflow_definition_id: workflowId,
      definition_name: DEV_WORKFLOW.name,
      definition_version: DEV_WORKFLOW.version,
      mode,
      workflow_trigger_id: null,
    },
  );
}

export function fetchWorkflowRuns(params: {
  status?: string;
  definitionId?: string;
  cursor?: string | null;
}): Promise<WorkflowRunsResponse> {
  const query = new URLSearchParams();
  if (params.status) query.set("status", params.status);
  if (params.definitionId) query.set("definition_id", params.definitionId);
  if (params.cursor) query.set("cursor", params.cursor);
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return withDevFallback(
    () => fetchJson<WorkflowRunsResponse>(`/api/portal/workflow-runs${suffix}`),
    {
      status: "ok",
      runs: [devRun({}), devRun({ workflow_run_id: "9f8e7d6c-0000-4000-8000-000000000002", status: "waiting_on_approval", mode: "live" })],
      count: 2,
      next_cursor: null,
    },
  );
}

export function fetchWorkflowRun(runId: string): Promise<WorkflowRunDetailResponse> {
  return withDevFallback(
    () => fetchJson<WorkflowRunDetailResponse>(`/api/portal/workflow-runs/${runId}`),
    runId === "9f8e7d6c-0000-4000-8000-000000000002"
      ? {
          status: "ok",
          run: devRun({
            workflow_run_id: runId,
            mode: "live",
            status: "waiting_on_approval",
            completed_at: null,
            current_step_node_id: "gate",
          }),
          steps: DEV_RUN_DETAIL.steps,
          approval: {
            workflow_run_approval_id: "aa1b2c3d-0000-4000-8000-000000000001",
            node_id: "gate",
            status: "pending",
            summary: "Send the coverage digest email for 4 new TTD documents?",
            approver_emails: ["dev@localhost"],
            proposed_actions: [],
            requested_at: new Date(Date.now() - 5 * 3600e3).toISOString(),
            expires_at: new Date(Date.now() + 67 * 3600e3).toISOString(),
            decided_at: null,
            decided_by: null,
            decision_comment: null,
            deep_link: "/app/approvals/aa1b2c3d-0000-4000-8000-000000000001",
          },
        }
      : DEV_RUN_DETAIL,
  );
}

export function cancelWorkflowRun(runId: string): Promise<WorkflowRunCancelResponse> {
  return withDevFallback(
    () => postJson<WorkflowRunCancelResponse>(`/api/portal/workflow-runs/${runId}/cancel`, {}),
    { status: "ok", workflow_run_id: runId, run_status: "cancelled" },
  );
}

// --- Approvals (C6: decision flow) -------------------------------------------

const DEV_APPROVAL_ID = "aa1b2c3d-0000-4000-8000-000000000001";
const DEV_WAITING_RUN_ID = "9f8e7d6c-0000-4000-8000-000000000002";

const DEV_APPROVAL_DETAIL: ApprovalDetailResponse = {
  status: "ok",
  approval: {
    workflow_run_approval_id: DEV_APPROVAL_ID,
    workflow_run_id: DEV_WAITING_RUN_ID,
    workflow_run_step_id: "step-gate",
    node_id: "gate",
    status: "pending",
    summary: "Send the coverage digest email for 4 new TTD documents?",
    approver_emails: ["dev@localhost"],
    proposed_actions: [
      {
        type: "notify.email",
        node_id: "notify",
        recipients: ["dev@localhost"],
        subject: "Coverage sentinel: 4 hits",
        body_text: "Search finished.",
        deep_link: null,
      },
    ],
    requested_at: new Date(Date.now() - 5 * 3600e3).toISOString(),
    expires_at: new Date(Date.now() + 67 * 3600e3).toISOString(),
    is_expired: false,
    decided_at: null,
    decided_by: null,
    decision_comment: null,
    deep_link: `/app/approvals/${DEV_APPROVAL_ID}`,
  },
  run: {
    workflow_run_id: DEV_WAITING_RUN_ID,
    workflow_id: DEV_WORKFLOW_ID,
    definition_name: "Coverage sentinel",
    definition_version: 1,
    mode: "live",
    status: "waiting_on_approval",
    trigger_reason: "manual",
    triggered_by: "dev@localhost",
    created_at: new Date(Date.now() - 5.2 * 3600e3).toISOString(),
  },
  approval_node: {
    node_id: "gate",
    config: { approver_emails: ["dev@localhost"], expires_hours: 72 },
  },
  pending_steps: [
    {
      node_id: "notify",
      node_type: "notify.email",
      config: {
        recipient_source: "run.triggered_by",
        subject_template: "Coverage sentinel: 4 hits",
        body_template: "Search finished.",
      },
      unresolved: [],
      snapshotted_action: {
        type: "notify.email",
        node_id: "notify",
        recipients: ["dev@localhost"],
        subject: "Coverage sentinel: 4 hits",
        body_text: "Search finished.",
        deep_link: null,
      },
    },
  ],
  snapshot_captured_at: new Date(Date.now() - 5 * 3600e3).toISOString(),
};

export function fetchApproval(approvalId: string): Promise<ApprovalDetailResponse> {
  return withDevFallback(
    () => fetchJson<ApprovalDetailResponse>(`/api/portal/approvals/${approvalId}`),
    DEV_APPROVAL_DETAIL,
  );
}

export function decideWorkflowRun(
  runId: string,
  approvalId: string,
  decision: "approve" | "reject",
  comment?: string,
): Promise<ApprovalDecisionResponse> {
  const body: { approval_id: string; comment?: string } = { approval_id: approvalId };
  if (comment && comment.trim()) body.comment = comment.trim();
  return withDevFallback(
    () =>
      postJson<ApprovalDecisionResponse>(
        `/api/portal/workflow-runs/${runId}/${decision}`,
        body,
      ),
    {
      status: "ok",
      workflow_run_id: runId,
      workflow_run_approval_id: approvalId,
      decision: decision === "approve" ? "approved" : "rejected",
      decided_by: "dev@localhost",
      resumed_at_node: decision === "approve" ? "notify" : null,
      run: { status: decision === "approve" ? "queued" : "cancelled" },
    },
  );
}

// --- Research, companies, portfolio, and operations -----------------------

const DEV_MEMOS: ResearchMemosResponse = {
  status: "ok",
  count: 3,
  memos: [
    {
      artifact_id: "memo-dev-1",
      request_id: "request-dev-1",
      artifact_type: "final_ic_memo",
      version: 3,
      status: "approved",
      title: "MU investment committee memo",
      created_by: "athena",
      approved_by: "dev@localhost",
      approved_at: new Date(Date.now() - 86400e3).toISOString(),
      created_at: new Date(Date.now() - 3 * 86400e3).toISOString(),
      updated_at: new Date(Date.now() - 86400e3).toISOString(),
      issuer_id: "issuer-dev-1",
      company_name: "Micron Technology",
      ticker: "MU",
      content_length: 18420,
    },
    {
      artifact_id: "memo-dev-2",
      request_id: "request-dev-2",
      artifact_type: "ic_packet",
      version: 1,
      status: "review_required",
      title: "TTD re-underwrite packet",
      created_by: "athena",
      approved_by: null,
      approved_at: null,
      created_at: new Date(Date.now() - 4 * 3600e3).toISOString(),
      updated_at: new Date(Date.now() - 4 * 3600e3).toISOString(),
      issuer_id: "issuer-dev-2",
      company_name: "The Trade Desk",
      ticker: "TTD",
      content_length: 11200,
    },
    {
      artifact_id: "memo-dev-ttd-approved",
      request_id: "request-dev-ttd-approved",
      artifact_type: "ic_packet",
      version: 1,
      status: "approved",
      title: "TTD investment committee packet — July review",
      created_by: "athena",
      approved_by: "dev@localhost",
      approved_at: new Date(Date.now() - 28 * 86400e3).toISOString(),
      created_at: new Date(Date.now() - 30 * 86400e3).toISOString(),
      updated_at: new Date(Date.now() - 28 * 86400e3).toISOString(),
      issuer_id: "issuer-dev-2",
      company_name: "The Trade Desk",
      ticker: "TTD",
      content_length: 10740,
    },
  ],
};

function devSeriesArtifact(
  artifactId: string,
  artifactType: string,
  status: string,
  title: string,
  daysAgo: number,
  version = 1,
): ResearchSeriesArtifact {
  const createdAt = new Date(Date.now() - daysAgo * 86400e3).toISOString();
  return {
    artifact_id: artifactId,
    artifact_type: artifactType,
    version,
    status,
    title,
    approved_at: status === "approved" ? createdAt : null,
    created_at: createdAt,
    updated_at: createdAt,
    content_length: 10000 + daysAgo,
  };
}

const DEV_RESEARCH_SERIES: ResearchSeriesResponse = {
  status: "ok",
  count: 3,
  next_cursor: null,
  series: [
    {
      company: { issuer_id: "issuer-dev-2", company_name: "The Trade Desk", ticker: "TTD" },
      artifact_type: "ic_packet",
      latest_artifact: devSeriesArtifact(
        "memo-dev-2",
        "ic_packet",
        "review_required",
        "TTD re-underwrite packet",
        0,
      ),
      latest_approved_artifact: devSeriesArtifact(
        "memo-dev-ttd-approved",
        "ic_packet",
        "approved",
        "TTD investment committee packet — July review",
        28,
      ),
      run_count: 52,
      earlier_run_count: 51,
    },
    {
      company: { issuer_id: "issuer-dev-2", company_name: "The Trade Desk", ticker: "TTD" },
      artifact_type: "counter_thesis",
      latest_artifact: devSeriesArtifact(
        "memo-dev-ttd-counter",
        "counter_thesis",
        "review_required",
        "TTD counter-thesis refresh",
        1,
      ),
      latest_approved_artifact: null,
      run_count: 18,
      earlier_run_count: 17,
    },
    {
      company: { issuer_id: "issuer-dev-1", company_name: "Micron Technology", ticker: "MU" },
      artifact_type: "final_ic_memo",
      latest_artifact: devSeriesArtifact(
        "memo-dev-1",
        "final_ic_memo",
        "approved",
        "MU investment committee memo",
        1,
        3,
      ),
      latest_approved_artifact: devSeriesArtifact(
        "memo-dev-1",
        "final_ic_memo",
        "approved",
        "MU investment committee memo",
        1,
        3,
      ),
      run_count: 6,
      earlier_run_count: 5,
    },
  ],
};

function devResearchSeries(
  filters: { q?: string; ticker?: string; cursor?: string | null },
): ResearchSeriesResponse {
  const query = filters.q?.trim().toLocaleLowerCase() ?? "";
  const ticker = filters.ticker?.trim().toUpperCase() ?? "";
  const series = DEV_RESEARCH_SERIES.series.filter((item) => {
    const text = `${item.company.company_name ?? ""} ${item.latest_artifact.title ?? ""}`.toLocaleLowerCase();
    return (!query || text.includes(query)) && (!ticker || item.company.ticker === ticker);
  });
  return { status: "ok", series, count: series.length, next_cursor: null };
}

function devResearchHistory(artifactId: string, cursor: string | null | undefined, limit = 25): ResearchHistoryResponse {
  const totalCount = artifactId.includes("counter") ? 18 : artifactId === "memo-dev-1" ? 6 : 52;
  const offset = cursor?.startsWith("dev-offset-") ? Number(cursor.slice("dev-offset-".length)) : 0;
  const safeOffset = Number.isInteger(offset) && offset >= 0 ? offset : 0;
  const pageSize = Math.min(Math.max(limit, 1), 100);
  const end = Math.min(safeOffset + pageSize, totalCount);
  const artifactType = artifactId.includes("counter") ? "counter_thesis" : artifactId === "memo-dev-1" ? "final_ic_memo" : "ic_packet";
  const ticker = artifactId === "memo-dev-1" ? "MU" : "TTD";
  const companyName = artifactId === "memo-dev-1" ? "Micron Technology" : "The Trade Desk";
  const issuerId = artifactId === "memo-dev-1" ? "issuer-dev-1" : "issuer-dev-2";
  const runs = Array.from({ length: end - safeOffset }, (_, pageIndex) => {
    const runIndex = safeOffset + pageIndex;
    const daysAgo = runIndex * 7;
    const status = runIndex % 4 === 3 ? "approved" : "review_required";
    return {
      request_type_label: runIndex === 0 ? "Full IC review" : "Rerun with new evidence",
      depth_label: runIndex % 3 === 0 ? "Full review" : "Focused refresh",
      requested_at: new Date(Date.now() - daysAgo * 86400e3).toISOString(),
      completed_at: new Date(Date.now() - Math.max(daysAgo - 1, 0) * 86400e3).toISOString(),
      version_count: runIndex % 8 === 0 ? 2 : 1,
      latest_artifact: devSeriesArtifact(
        runIndex === 0 ? artifactId : `${artifactId}-run-${runIndex + 1}`,
        artifactType,
        status,
        `${ticker} ${artifactType === "ic_packet" ? "IC packet" : artifactType === "counter_thesis" ? "counter-thesis" : "investment memo"} — run ${totalCount - runIndex}`,
        daysAgo,
        runIndex % 8 === 0 ? 2 : 1,
      ),
    };
  });
  return {
    status: "ok",
    company: { issuer_id: issuerId, company_name: companyName, ticker },
    artifact_type: artifactType,
    runs,
    count: runs.length,
    total_count: totalCount,
    next_cursor: end < totalCount ? `dev-offset-${end}` : null,
  };
}

export function fetchResearchMemos(filters: { q?: string; ticker?: string } = {}): Promise<ResearchMemosResponse> {
  const query = new URLSearchParams();
  if (filters.q) query.set("q", filters.q);
  if (filters.ticker) query.set("ticker", filters.ticker);
  const suffix = query.size ? `?${query.toString()}` : "";
  return withDevFallback(
    () => fetchJson<ResearchMemosResponse>(`/api/portal/research${suffix}`),
    DEV_MEMOS,
  );
}

export function fetchResearchSeries(
  filters: { q?: string; ticker?: string; limit?: number; cursor?: string | null } = {},
): Promise<ResearchSeriesResponse> {
  const query = new URLSearchParams();
  if (filters.q) query.set("q", filters.q);
  if (filters.ticker) query.set("ticker", filters.ticker);
  if (filters.limit) query.set("limit", String(filters.limit));
  if (filters.cursor) query.set("cursor", filters.cursor);
  const suffix = query.size ? `?${query.toString()}` : "";
  return withDevFallback(
    () => fetchJson<ResearchSeriesResponse>(`/api/portal/research/series${suffix}`),
    devResearchSeries(filters),
  );
}

export function fetchResearchHistory(
  artifactId: string,
  params: { limit?: number; cursor?: string | null } = {},
): Promise<ResearchHistoryResponse> {
  const query = new URLSearchParams();
  if (params.limit) query.set("limit", String(params.limit));
  if (params.cursor) query.set("cursor", params.cursor);
  const suffix = query.size ? `?${query.toString()}` : "";
  return withDevFallback(
    () => fetchJson<ResearchHistoryResponse>(
      `/api/portal/research/${encodeURIComponent(artifactId)}/history${suffix}`,
    ),
    devResearchHistory(artifactId, params.cursor, params.limit),
  );
}

export function fetchResearchMemo(artifactId: string): Promise<ResearchMemoResponse> {
  const summary = DEV_MEMOS.memos.find((memo) => memo.artifact_id === artifactId) ?? DEV_MEMOS.memos[0];
  return withDevFallback(
    () => fetchJson<ResearchMemoResponse>(`/api/portal/research/${encodeURIComponent(artifactId)}`),
    {
      status: "ok",
      memo: {
        ...summary,
        content_markdown:
          "# Current investment view\n\nMicron is entering the next memory cycle with improving mix and disciplined supply.\n\n## What matters\n\n- HBM execution and qualification pace\n- DRAM pricing durability\n- Capital intensity through the cycle",
        content_json: {},
        review_notes: null,
        metadata: {},
        has_pdf: false,
      },
      versions: [
        { artifact_id: summary.artifact_id, version: summary.version, status: summary.status, title: summary.title, updated_at: summary.updated_at },
      ],
    },
  );
}

const DEV_COMPANIES: CompaniesResponse = {
  status: "ok",
  generated_at: new Date().toISOString(),
  summary: { company_count: 3, portfolio_count: 1, active_bench_count: 1, universe_count: 1 },
  count: 3,
  total_count: 3,
  next_cursor: null,
  companies: [
    { research_subject_tier_id: "tier-1", tier: "portfolio", status: "active", priority: "high", owner: null, rationale: "Core memory exposure", issuer_id: "11111111-1111-4111-8111-111111111111", company_name: "Micron Technology", ticker: "MU", document_count: 124, latest_document_seen_at: new Date().toISOString(), tracked_model_count: 1, company_url: "/app/companies/11111111-1111-4111-8111-111111111111" },
    { research_subject_tier_id: "tier-2", tier: "active_bench", status: "active", priority: "normal", owner: null, rationale: null, issuer_id: "22222222-2222-4222-8222-222222222222", company_name: "The Trade Desk", ticker: "TTD", document_count: 87, latest_document_seen_at: new Date().toISOString(), tracked_model_count: 1, company_url: "/app/companies/22222222-2222-4222-8222-222222222222" },
    { research_subject_tier_id: "tier-3", tier: "universe", status: "active", priority: "normal", owner: null, rationale: null, issuer_id: "33333333-3333-4333-8333-333333333333", company_name: "McDonald's", ticker: "MCD", document_count: 61, latest_document_seen_at: new Date().toISOString(), tracked_model_count: 0, company_url: "/app/companies/33333333-3333-4333-8333-333333333333" },
  ],
};

function devCompanies(
  filters: { q?: string; tier?: string; limit?: number; cursor?: string | null },
): CompaniesResponse {
  const normalizedQuery = filters.q?.trim().toLowerCase() ?? "";
  const filtered = DEV_COMPANIES.companies.filter((company) => {
    const matchesQuery = !normalizedQuery
      || String(company.ticker ?? "").toLowerCase().includes(normalizedQuery)
      || String(company.company_name ?? "").toLowerCase().includes(normalizedQuery);
    return matchesQuery && (!filters.tier || company.tier === filters.tier);
  });
  const cursorMatch = /^dev-company-(\d+)$/.exec(filters.cursor ?? "");
  const offset = cursorMatch ? Number(cursorMatch[1]) : 0;
  const limit = Math.max(1, Math.min(100, filters.limit ?? 50));
  const companies = filtered.slice(offset, offset + limit);
  const nextOffset = offset + companies.length;

  return {
    status: "ok",
    generated_at: DEV_COMPANIES.generated_at,
    summary: {
      company_count: filtered.length,
      portfolio_count: filtered.filter((company) => company.tier === "portfolio").length,
      active_bench_count: filtered.filter((company) => company.tier === "active_bench").length,
      universe_count: filtered.filter((company) => company.tier === "universe").length,
    },
    companies,
    count: companies.length,
    total_count: filtered.length,
    next_cursor: nextOffset < filtered.length ? `dev-company-${nextOffset}` : null,
  };
}

export function fetchCompanies(
  filters: { q?: string; tier?: string; limit?: number; cursor?: string | null } = {},
  signal?: AbortSignal,
): Promise<CompaniesResponse> {
  const query = new URLSearchParams();
  if (filters.q?.trim()) query.set("q", filters.q.trim());
  if (filters.tier) query.set("tier", filters.tier);
  if (filters.limit) query.set("limit", String(Math.max(1, Math.min(100, filters.limit))));
  if (filters.cursor) query.set("cursor", filters.cursor);
  const suffix = query.size ? `?${query.toString()}` : "";
  return withDevFallback(
    () => fetchJson<CompaniesResponse>(`/api/portal/companies${suffix}`, { signal }),
    devCompanies(filters),
  );
}

export function fetchCompanySearch(
  params: { q: string; limit?: number },
  signal?: AbortSignal,
): Promise<CompanySearchResponse> {
  const query = new URLSearchParams();
  query.set("q", params.q.trim());
  query.set("limit", String(Math.max(1, Math.min(25, params.limit ?? 10))));
  const normalized = params.q.trim().toLowerCase();
  const fixture: CompanySearchResponse = {
    status: "ok",
    query: params.q.trim(),
    provider_status: { available: true, reason: "available" },
    results: DEV_COMPANIES.companies
      .filter((company) =>
        String(company.ticker ?? "").toLowerCase().includes(normalized)
        || String(company.company_name ?? "").toLowerCase().includes(normalized),
      )
      .slice(0, params.limit ?? 10)
      .map((company) => ({
        issuer_id: company.issuer_id,
        security_id: null,
        listing_id: null,
        ticker: company.ticker,
        provider_display_ticker: company.ticker,
        company_name: company.company_name ?? company.ticker ?? "Unknown company",
        exchange: null,
        mic: null,
        country: null,
        currency: null,
        security_type: "equity",
        is_primary: true,
        tier: company.tier,
        tracked: true,
        monitor_eligible: true,
        document_count: company.document_count,
        company_url: company.company_url,
      })),
    count: 0,
  };
  fixture.count = fixture.results.length;
  return withDevFallback(
    () => fetchJson<CompanySearchResponse>(`/api/portal/company-search?${query.toString()}`, { signal }),
    fixture,
  );
}

function devCompanyDetail(companyRef: string): CompanyDetailResponse {
  const company = DEV_COMPANIES.companies.find(
    (row) => row.issuer_id === companyRef || row.ticker === companyRef,
  ) ?? DEV_COMPANIES.companies[0];
  return {
    status: "ok",
    generated_at: new Date().toISOString(),
    company: { issuer_id: company.issuer_id, ticker: company.ticker, company_name: company.company_name },
    tier: { tier: company.tier, priority: company.priority, rationale: company.rationale },
    documents: [{ document_id: "doc-dev-1", title: "Latest company update", source_name: "jpmorgan", doc_date: new Date().toISOString().slice(0, 10), page_count: 18, document_link_url: "/app/documents/doc-dev-1" }],
    primary_tie_outs: [],
    evidence_gaps: [],
    models: [],
    challengers: [
      { challenger_relationship_id: "challenger-dev-1", relationship_type: "direct_competitor", rank: 1, status: "active", held_issuer_id: company.issuer_id, held_name: company.company_name, held_ticker: company.ticker, challenger_issuer_id: "issuer-9", challenger_name: "SK Hynix", challenger_ticker: "000660.KS", rationale: "Closest HBM capacity comparison" },
    ],
    workflow_requests: [],
    workflow_artifacts: [],
    summary: {},
  };
}

export function fetchCompany(companyRef: string): Promise<CompanyDetailResponse> {
  return withDevFallback(
    () => fetchJson<CompanyDetailResponse>(`/api/portal/companies/${encodeURIComponent(companyRef)}`),
    devCompanyDetail(companyRef),
  );
}

export function fetchCompanyHub(companyRef: string): Promise<CompanyDetailResponse> {
  return withDevFallback(
    () => fetchJson<CompanyDetailResponse>(
      `/api/portal/companies/${encodeURIComponent(companyRef)}?view=hub`,
    ),
    devCompanyDetail(companyRef),
  );
}

export function trackCompany(
  companyRef: string,
  body: { tier: "universe" },
): Promise<CompanyTrackResponse> {
  const company = DEV_COMPANIES.companies.find(
    (row) => row.issuer_id === companyRef || row.ticker === companyRef,
  ) ?? DEV_COMPANIES.companies[0];
  return withDevFallback(
    () => postJson<CompanyTrackResponse>(
      `/api/portal/companies/${encodeURIComponent(companyRef)}/track`,
      body,
    ),
    {
      status: "ok",
      result_type: "provider_company_onboarded",
      provider: { name: "fmp", reference: companyRef, symbol: company.ticker ?? companyRef },
      universe_slug: "portal-global-companies",
      company: {
        issuer_id: company.issuer_id,
        security_id: null,
        listing_id: null,
        ticker: company.ticker,
        company_name: company.company_name,
        company_url: `/app/companies/${encodeURIComponent(company.issuer_id)}`,
      },
      membership: { action: "inserted", status: "active" },
      tier: { tier: body.tier },
      identity_enrichment: {},
      acquisition: { status: "not_started" },
    },
  );
}

export function fetchCompanyReadiness(
  companyRef: string,
  signal?: AbortSignal,
): Promise<CompanyReadinessResponse> {
  const company = DEV_COMPANIES.companies.find(
    (row) => row.issuer_id === companyRef || row.ticker === companyRef,
  ) ?? DEV_COMPANIES.companies[0];
  const generatedAt = new Date().toISOString();
  return withDevFallback(
    () => fetchJson<CompanyReadinessResponse>(
      `/api/portal/companies/${encodeURIComponent(companyRef)}/readiness`,
      { signal },
    ),
    {
      status: "ok",
      company: {
        issuer_id: company.issuer_id,
        ticker: company.ticker,
        company_name: company.company_name,
        company_url: `/app/companies/${encodeURIComponent(company.issuer_id)}`,
      },
      acquisition: {
        status: "ready",
        active_job_count: 0,
        failed_job_count: 0,
        latest_activity_at: generatedAt,
      },
      readiness: {
        readiness_status: "ready",
        generated_at: generatedAt,
        source_checks: [
          { source: "sec_primary_filings", label: "SEC primary filings", status: "ready", document_count: 8 },
          { source: "institutional_research", label: "Broker research", status: "ready", document_count: 4 },
        ],
      },
    },
  );
}

export function fetchCompanyDocuments(
  companyRef: string,
  params: {
    page?: number;
    page_size?: number;
    q?: string;
    source?: string;
    doc_type?: string;
  } = {},
  signal?: AbortSignal,
): Promise<CompanyDocumentsResponse> {
  const query = new URLSearchParams();
  query.set("page", String(Math.max(1, params.page ?? 1)));
  query.set("page_size", String(Math.max(1, Math.min(100, params.page_size ?? 20))));
  if (params.q) query.set("q", params.q);
  if (params.source) query.set("source", params.source);
  if (params.doc_type) query.set("doc_type", params.doc_type);
  const company = DEV_COMPANIES.companies.find(
    (row) => row.issuer_id === companyRef || row.ticker === companyRef,
  ) ?? DEV_COMPANIES.companies[0];
  return withDevFallback(
    () => fetchJson<CompanyDocumentsResponse>(
      `/api/portal/companies/${encodeURIComponent(companyRef)}/documents?${query.toString()}`,
      { signal },
    ),
    {
      status: "ok",
      company: { issuer_id: company.issuer_id, ticker: company.ticker, company_name: company.company_name },
      filters: { q: params.q ?? "", source: params.source ?? "", doc_type: params.doc_type ?? "" },
      pagination: {
        page: params.page ?? 1,
        page_size: params.page_size ?? 20,
        total: 1,
        total_pages: 1,
        has_previous: false,
        has_next: false,
      },
      count: 1,
      documents: [{
        document_id: "doc-dev-1",
        title: "Latest company update",
        source_name: "jpmorgan",
        provider: "jpmorgan",
        doc_type: "broker_note",
        doc_date: new Date().toISOString().slice(0, 10),
        published_at: null,
        created_at: new Date().toISOString(),
        page_count: 18,
        document_link_url: "/app/documents/doc-dev-1",
        match_kind: "issuer",
      }],
    },
  );
}

export function setCompanyTier(
  companyRef: string,
  body: { tier: string; status?: string; priority?: string; rationale?: string; owner?: string },
): Promise<CompanyTierSetResponse> {
  return withDevFallback(
    () => postJson<CompanyTierSetResponse>(`/api/portal/companies/${encodeURIComponent(companyRef)}/tier`, body),
    {
      status: "ok",
      ticker: companyRef,
      tier: { tier: body.tier, status: body.status ?? "active", priority: body.priority ?? "normal", rationale: body.rationale ?? null },
      position: null,
    },
  );
}

export function updateCompanyEvidenceGaps(
  companyRef: string,
  gaps: Array<{ gap_id: string; status?: string; severity?: string; gap_text?: string }>,
): Promise<EvidenceGapUpdateResponse> {
  return postJson<EvidenceGapUpdateResponse>(
    `/api/portal/companies/${encodeURIComponent(companyRef)}/evidence-gaps`,
    { gaps },
  );
}

// --- Documents browse (Wave 2.2) --------------------------------------------

const DEV_BROWSE_DOCUMENTS: DocumentsBrowseResponse["documents"] = [
  {
    document_id: DEV_DOCUMENT_ID,
    external_document_id: "citi_11064252",
    source_type: "institutional_research",
    source_name: "citi",
    provider: "citi",
    ticker: "TTD",
    company_name: "The Trade Desk",
    title: "Navigating Macro Headwinds — Ad Agency Survey",
    doc_date: "2026-08-08",
    published_at: "2026-08-08T06:30:00Z",
    doc_type: "research_note",
    language: "en",
    page_count: 24,
    chunk_count: 61,
    quality_status: "ok",
    ingested_at: "2026-08-08T07:05:00Z",
    is_supporting_artifact: false,
    deep_link: `/app/documents/${DEV_DOCUMENT_ID}`,
  },
  {
    document_id: "dev-doc-mu-hbm",
    external_document_id: "ms_morning_20260808_mu",
    source_type: "institutional_research",
    source_name: "Morgan Stanley Research",
    provider: "morgan_stanley",
    ticker: "MU",
    company_name: "Micron Technology",
    title: "HBM supply outlook: qualification milestones and pricing",
    doc_date: "2026-08-08",
    published_at: "2026-08-08T05:15:00Z",
    doc_type: "research_note",
    language: "en",
    page_count: 18,
    chunk_count: 43,
    quality_status: "ok",
    ingested_at: "2026-08-08T06:02:00Z",
    is_supporting_artifact: false,
    deep_link: "/app/documents/dev-doc-mu-hbm",
  },
  {
    document_id: "dev-doc-asml-transcript",
    external_document_id: "asml_q2_2026_transcript",
    source_type: "company_disclosure",
    source_name: "Company filings",
    provider: "asml",
    ticker: "ASML",
    company_name: "ASML Holding",
    title: "Q2 2026 earnings call transcript",
    doc_date: "2026-08-07",
    published_at: "2026-08-07T15:00:00Z",
    doc_type: "earnings_transcript",
    language: "en",
    page_count: 31,
    chunk_count: 79,
    quality_status: "verified",
    ingested_at: "2026-08-08T04:48:00Z",
    is_supporting_artifact: false,
    deep_link: "/app/documents/dev-doc-asml-transcript",
  },
  {
    document_id: "dev-doc-mcd-traffic",
    external_document_id: "jpm_us_restaurant_traffic_0726",
    source_type: "alternative_data",
    source_name: "JPMorgan Research",
    provider: "jpmorgan",
    ticker: "MCD",
    company_name: "McDonald's",
    title: "US restaurant traffic tracker — July",
    doc_date: "2026-08-07",
    published_at: "2026-08-07T12:20:00Z",
    doc_type: "alternative_data",
    language: "en",
    page_count: 12,
    chunk_count: 29,
    quality_status: "ok",
    ingested_at: "2026-08-08T03:25:00Z",
    is_supporting_artifact: false,
    deep_link: "/app/documents/dev-doc-mcd-traffic",
  },
  {
    document_id: "dev-doc-san-results",
    external_document_id: "san_h1_2026_results",
    source_type: "company_disclosure",
    source_name: "Company filings",
    provider: "santander",
    ticker: "SAN",
    company_name: "Banco Santander",
    title: "Half-year results and capital update",
    doc_date: "2026-08-06",
    published_at: "2026-08-06T16:00:00Z",
    doc_type: "results_presentation",
    language: "en",
    page_count: 42,
    chunk_count: 108,
    quality_status: "ok",
    ingested_at: "2026-08-07T21:10:00Z",
    is_supporting_artifact: false,
    deep_link: "/app/documents/dev-doc-san-results",
  },
  {
    document_id: "dev-doc-aapl-10q",
    external_document_id: "sec_aapl_10q_2026q3",
    source_type: "regulatory_filing",
    source_name: "SEC",
    provider: "sec",
    ticker: "AAPL",
    company_name: "Apple",
    title: "Form 10-Q — quarter ended June 27, 2026",
    doc_date: "2026-08-06",
    published_at: "2026-08-06T20:05:00Z",
    doc_type: "filing",
    language: "en",
    page_count: 68,
    chunk_count: 184,
    quality_status: "ok",
    ingested_at: "2026-08-07T18:40:00Z",
    is_supporting_artifact: false,
    deep_link: "/app/documents/dev-doc-aapl-10q",
  },
  {
    document_id: "dev-doc-nvda-blackwell",
    external_document_id: "bernstein_nvda_blackwell_0807",
    source_type: "institutional_research",
    source_name: "Bernstein Research",
    provider: "bernstein",
    ticker: "NVDA",
    company_name: "NVIDIA",
    title: "Blackwell supply tracker: rack-scale delivery cadence",
    doc_date: "2026-08-07",
    published_at: "2026-08-07T07:30:00Z",
    doc_type: "research_note",
    language: "en",
    page_count: 15,
    chunk_count: 36,
    quality_status: "review",
    ingested_at: "2026-08-07T09:18:00Z",
    is_supporting_artifact: false,
    deep_link: "/app/documents/dev-doc-nvda-blackwell",
  },
  {
    document_id: "dev-doc-tsm-revenue",
    external_document_id: "tsm_monthly_revenue_202607",
    source_type: "company_disclosure",
    source_name: "Company filings",
    provider: "tsmc",
    ticker: "TSM",
    company_name: "Taiwan Semiconductor Manufacturing",
    title: "July 2026 monthly revenue report",
    doc_date: "2026-08-07",
    published_at: "2026-08-07T06:00:00Z",
    doc_type: "financial_update",
    language: "en",
    page_count: 4,
    chunk_count: 8,
    quality_status: "ok",
    ingested_at: "2026-08-07T06:24:00Z",
    is_supporting_artifact: false,
    deep_link: "/app/documents/dev-doc-tsm-revenue",
  },
];

function devDocumentsBrowse(params: {
  q?: string;
  ticker?: string;
  source?: string;
  page?: number;
}): DocumentsBrowseResponse {
  const q = params.q?.trim().toLowerCase() ?? "";
  const ticker = params.ticker?.trim().toUpperCase() ?? "";
  const source = params.source?.trim().toLowerCase() ?? "";
  const page = Math.max(1, params.page ?? 1);
  const pageSize = 50;
  const filtered = DEV_BROWSE_DOCUMENTS.filter((document) => {
    const searchable = [
      document.title,
      document.company_name,
      document.external_document_id,
      document.doc_type,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    const sourceText = [document.source_name, document.source_type, document.provider]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return (
      (!q || searchable.includes(q)) &&
      (!ticker || document.ticker?.toUpperCase() === ticker) &&
      (!source || sourceText.includes(source))
    );
  });
  const start = (page - 1) * pageSize;
  const pageDocuments = filtered.slice(start, start + pageSize);
  const totalPages = filtered.length === 0 ? 0 : Math.ceil(filtered.length / pageSize);

  return {
    status: "ok",
    generated_at: new Date().toISOString(),
    filters: { q, ticker, source, include_supporting: false },
    pagination: {
      page,
      page_size: pageSize,
      total: filtered.length,
      total_pages: totalPages,
      has_previous: page > 1,
      has_next: page < totalPages,
    },
    count: pageDocuments.length,
    documents: pageDocuments,
  };
}

export function fetchDocumentsBrowse(params: {
  q?: string;
  ticker?: string;
  source?: string;
  page?: number;
}): Promise<DocumentsBrowseResponse> {
  const search = new URLSearchParams();
  if (params.q) search.set("q", params.q);
  if (params.ticker) search.set("ticker", params.ticker);
  if (params.source) search.set("source", params.source);
  if (params.page && params.page > 1) search.set("page", String(params.page));
  const suffix = search.toString() ? `?${search.toString()}` : "";
  return withDevFallback(
    () => fetchJson<DocumentsBrowseResponse>(`/api/portal/documents${suffix}`),
    devDocumentsBrowse(params),
  );
}

const DEV_PORTFOLIO: PortfolioResponse = {
  status: "ok",
  books: [{ book_id: "book-dev-1", slug: "core-book", name: "Core Book", description: null, base_currency: "USD", status: "active", owner: "dev@localhost", created_at: new Date().toISOString(), updated_at: new Date().toISOString() }],
  selected_book_id: "book-dev-1",
  positions: [
    { position_id: "position-dev-1", book_id: "book-dev-1", issuer_id: "issuer-1", ticker: "MU", company_name: "Micron Technology", side: "long", status: "active", weight_bps: 650, target_weight_bps: 800, note: "HBM cycle", opened_at: "2026-01-10", updated_at: new Date().toISOString() },
    { position_id: "position-dev-2", book_id: "book-dev-1", issuer_id: null, ticker: "USD", company_name: "USD cash", side: "cash", status: "active", weight_bps: 1200, target_weight_bps: null, note: null, opened_at: "2026-01-01", updated_at: new Date().toISOString() },
  ],
  summary: { book_count: 1, position_count: 2, gross_weight_bps: 1850, net_weight_bps: 1850 },
};

export function fetchPortfolio(bookId?: string): Promise<PortfolioResponse> {
  const suffix = bookId ? `?book_id=${encodeURIComponent(bookId)}` : "";
  return withDevFallback(() => fetchJson<PortfolioResponse>(`/api/portal/portfolio${suffix}`), DEV_PORTFOLIO);
}

export function createPortfolioBook(body: { name: string; base_currency?: string; description?: string }): Promise<PortfolioBookResponse> {
  return withDevFallback(
    () => postJson<PortfolioBookResponse>("/api/portal/portfolio/books", body),
    { status: "ok", book: { ...DEV_PORTFOLIO.books[0], book_id: "book-dev-new", name: body.name, base_currency: body.base_currency ?? "USD" } },
  );
}

export function updatePortfolioBook(bookId: string, body: { name?: string; base_currency?: string; description?: string; status?: string }): Promise<PortfolioBookResponse> {
  return withDevFallback(
    () => patchJson<PortfolioBookResponse>(`/api/portal/portfolio/books/${bookId}`, body),
    { status: "ok", book: { ...DEV_PORTFOLIO.books[0], ...body, book_id: bookId } },
  );
}

export function deletePortfolioBook(bookId: string): Promise<PortfolioMutationResponse> {
  return withDevFallback(
    () => deleteJson<PortfolioMutationResponse>(`/api/portal/portfolio/books/${bookId}`),
    { status: "ok", book_id: bookId, archived: true },
  );
}

export function createPortfolioPosition(bookId: string, body: { ticker: string; side: PositionSide; weight_bps?: number | null; target_weight_bps?: number | null; note?: string }): Promise<PortfolioMutationResponse> {
  return withDevFallback(
    () => postJson<PortfolioMutationResponse>(`/api/portal/portfolio/books/${bookId}/positions`, body),
    { status: "ok", position_id: "position-dev-new" },
  );
}

export function updatePortfolioPosition(positionId: string, body: { side?: PositionSide; weight_bps?: number | null; target_weight_bps?: number | null; note?: string; status?: string }): Promise<PortfolioMutationResponse> {
  return withDevFallback(
    () => patchJson<PortfolioMutationResponse>(`/api/portal/portfolio/positions/${positionId}`, body),
    { status: "ok", position_id: positionId },
  );
}

export function deletePortfolioPosition(positionId: string): Promise<PortfolioMutationResponse> {
  return withDevFallback(
    () => deleteJson<PortfolioMutationResponse>(`/api/portal/portfolio/positions/${positionId}`),
    { status: "ok", position_id: positionId, removed: true },
  );
}

const DEV_OPERATIONS: OperationsResponse = {
  status: "ok",
  generated_at: new Date().toISOString(),
  summary: { running_count: 2, queued_count: 4, failed_count: 1, stale_count: 0, attention_count: 1 },
  workers: [{ portal_name: "jpmorgan", status: "authenticated", worker_id: "athena-mac-mini", authenticated: true, checked_at: new Date().toISOString(), age_seconds: 42, message: "Ready", action_needed: null }],
  queues: { database: [{ id: "capture_requests", name: "Portal captures", status: "running", queued_count: 2, running_count: 1, failed_recent_count: 1, last_activity_at: new Date().toISOString() }], sqs: [{ id: "capture_requests", name: "Capture request queue", visible: 2, in_flight: 1, delayed: 0 }] },
  runs: { active: [{ kind: "capture_request", id: "run-dev-1", label: "MU research", status: "running", priority: "normal", duration_seconds: 91, last_error: null, activity_at: new Date().toISOString() }], stale: [], recent_failures: [] },
  ingestion: [{ id: "capture_requests", name: "Portal captures", total: 7, counts: [{ status: "completed", count: 5 }, { status: "running", count: 2 }] }],
  alerts: [{ kind: "capture_request", label: "MCD research", status: "failed", error: "Portal session expired", at: new Date(Date.now() - 3600e3).toISOString() }],
  errors: [],
};

export function fetchOperations(): Promise<OperationsResponse> {
  return withDevFallback(() => fetchJson<OperationsResponse>("/api/portal/operations"), DEV_OPERATIONS);
}

// --- Manual workflow actions (Wave 2.4) --------------------------------------

export function fetchWorkflowActionCatalog(): Promise<WorkflowActionCatalogResponse> {
  return fetchJson<WorkflowActionCatalogResponse>("/api/portal/workflow-actions");
}

export function createCompanyWorkflowRequest(
  companyRef: string,
  body: WorkflowActionRequestBody,
): Promise<WorkflowActionCreateResponse> {
  return postJson<WorkflowActionCreateResponse>(
    `/api/portal/companies/${encodeURIComponent(companyRef)}/workflow-requests`,
    body,
  );
}

// --- Workflow artifacts (Wave 2.5: the scope-approval gate) ------------------

export function approveWorkflowArtifact(
  requestId: string,
  artifactId: string,
  body: { content_markdown?: string; review_notes?: string },
): Promise<WorkflowArtifactMutationResponse> {
  return postJson<WorkflowArtifactMutationResponse>(
    `/api/portal/workflow-requests/${encodeURIComponent(requestId)}/artifacts/${encodeURIComponent(artifactId)}/approve`,
    body,
  );
}
